import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, Plus, Trash2, Save, X, Edit3, Download, Heart, Mail, Mic2, Play, Pause, Star, ImageIcon, Music, Upload, Inbox, ExternalLink, StickyNote } from "lucide-react";
import ArchiveManager from "@/pages/admin/ArchiveManager";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "drr_admin_token";

export default function AdminPage() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [checking, setChecking] = useState(true);

  // Verify token on mount
  useEffect(() => {
    let mounted = true;
    const verify = async () => {
      if (!token) {
        if (mounted) setChecking(false);
        return;
      }
      try {
        await axios.get(`${API}/admin/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (mounted) setChecking(false);
      } catch (e) {
        localStorage.removeItem(TOKEN_KEY);
        if (mounted) {
          setToken(null);
          setChecking(false);
        }
      }
    };
    verify();
    return () => {
      mounted = false;
    };
  }, [token]);

  const handleLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    toast("Logged out.");
  };

  if (checking) {
    return (
      <main className="min-h-screen bg-zinc-950 text-zinc-100 flex items-center justify-center font-mono text-xs tracking-widest uppercase text-zinc-500">
        //_authenticating...
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-zinc-950 text-zinc-100" data-testid="admin-page">
      <header className="border-b-2 border-red-600 px-4 md:px-10 py-4 flex items-center justify-between bg-black">
        <div className="flex items-center gap-3">
          <span className="on-air-dot" aria-hidden />
          <span className="font-display text-xl tracking-tight">
            DRR <span className="text-red-600">CONTROL</span> PANEL
          </span>
          <span className="text-[10px] text-zinc-500 tracking-widest uppercase ml-2 hidden md:inline">
            // ADMIN
          </span>
        </div>
        {token && (
          <button
            onClick={handleLogout}
            className="text-xs tracking-widest uppercase text-zinc-300 hover:text-red-500 flex items-center gap-2"
            data-testid="admin-logout-btn"
          >
            <LogOut className="w-3 h-3" /> Logout
          </button>
        )}
      </header>
      {token ? (
        <>
          <DailyPulseCard
            token={token}
            onUnauthorized={() => {
              localStorage.removeItem(TOKEN_KEY);
              setToken(null);
            }}
          />
          <StatsBackupCard
            token={token}
            onUnauthorized={() => {
              localStorage.removeItem(TOKEN_KEY);
              setToken(null);
            }}
          />
          <SpinHistoryCard
            token={token}
            onUnauthorized={() => {
              localStorage.removeItem(TOKEN_KEY);
              setToken(null);
            }}
          />
          <Top10EmailCard
            token={token}
            onUnauthorized={() => {
              localStorage.removeItem(TOKEN_KEY);
              setToken(null);
            }}
          />
          <JingleStudioCard
            token={token}
            onUnauthorized={() => {
              localStorage.removeItem(TOKEN_KEY);
              setToken(null);
            }}
          />
          <RiffLibraryCard
            token={token}
            onUnauthorized={() => {
              localStorage.removeItem(TOKEN_KEY);
              setToken(null);
            }}
          />
          <ChatStickersCard
            token={token}
            onUnauthorized={() => {
              localStorage.removeItem(TOKEN_KEY);
              setToken(null);
            }}
          />
          <SubmissionsInboxCard
            token={token}
            onUnauthorized={() => {
              localStorage.removeItem(TOKEN_KEY);
              setToken(null);
            }}
          />
          <GigsInboxCard
            token={token}
            onUnauthorized={() => {
              localStorage.removeItem(TOKEN_KEY);
              setToken(null);
            }}
          />
          <FamilyWelcomeCard
            token={token}
            onUnauthorized={() => {
              localStorage.removeItem(TOKEN_KEY);
              setToken(null);
            }}
          />
          <WeeklyDigestCard
            token={token}
            onUnauthorized={() => {
              localStorage.removeItem(TOKEN_KEY);
              setToken(null);
            }}
          />
          <BandsManager
            token={token}
            onUnauthorized={() => {
              localStorage.removeItem(TOKEN_KEY);
              setToken(null);
            }}
          />
          <ArchiveManager
            token={token}
            onUnauthorized={() => {
              localStorage.removeItem(TOKEN_KEY);
              setToken(null);
            }}
          />
        </>
      ) : (
        <LoginForm
          onLogin={(t) => {
            localStorage.setItem(TOKEN_KEY, t);
            setToken(t);
          }}
        />
      )}
    </main>
  );
}


/**
 * Manual backup card on the admin page. One click hits /admin/export-stats
 * and downloads a portable JSON snapshot of:
 *  - all-time visitor counter
 *  - every geolocated session pin (city/region/country/lat/lng)
 *  - country + city aggregate counts
 * Keeps the user's listener history safe from platform migrations.
 */
function StatsBackupCard({ token, onUnauthorized }) {
  const [busy, setBusy] = useState(false);
  const [lastTotals, setLastTotals] = useState(null);

  const handleExport = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const res = await axios.get(`${API}/admin/export-stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = res.data;
      // Trigger a real browser download of the JSON file.
      const blob = new Blob([JSON.stringify(data, null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const today = new Date().toISOString().slice(0, 10);
      const a = document.createElement("a");
      a.href = url;
      a.download = `drr-stats-backup-${today}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setLastTotals(data.totals || null);
      toast.success("Backup downloaded — keep it safe!");
    } catch (err) {
      if (err?.response?.status === 401) {
        onUnauthorized?.();
        toast.error("Session expired. Log in again.");
      } else {
        toast.error("Export failed — try again");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      className="px-4 md:px-10 pt-6"
      data-testid="stats-backup-card"
    >
      <div className="border-2 border-yellow-400 bg-black/60 p-5 md:p-6 flex flex-col md:flex-row md:items-center gap-5">
        <div className="flex-1 min-w-0">
          <div className="text-[10px] tracking-[0.35em] text-yellow-400 uppercase mb-1">
            //_LISTENER_DATA_BACKUP
          </div>
          <h2 className="font-display text-2xl md:text-3xl text-zinc-50 leading-tight tracking-tight">
            PROTECT YOUR <span className="text-yellow-400">NUMBERS</span>.
          </h2>
          <p className="text-sm text-zinc-300 mt-2 leading-relaxed max-w-xl">
            Download a complete snapshot of your visitor counter + every globe
            pin. Auto-backups also email to your station inbox every Sunday at
            3 AM. Save them in Google Drive / Dropbox — your stats survive any
            platform change.
          </p>
          {lastTotals && (
            <div className="mt-3 text-xs text-zinc-400 tracking-widest uppercase font-mono">
              //_last_export: {lastTotals.all_time_visitors} visitors ·{" "}
              {lastTotals.unique_countries} countries ·{" "}
              {lastTotals.unique_cities} cities
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={handleExport}
          disabled={busy}
          className="bg-yellow-400 text-black hover:bg-zinc-50 disabled:opacity-50 disabled:cursor-not-allowed font-display tracking-[0.25em] uppercase text-xs px-5 py-3 flex items-center gap-2 transition-colors shrink-0"
          data-testid="export-stats-btn"
        >
          <Download className="w-4 h-4" strokeWidth={2.5} />
          {busy ? "Exporting…" : "Export Listener Data"}
        </button>
      </div>
    </section>
  );
}


/**
 * Spin History card — shows headline counters (unique artists / total plays)
 * and a "Download CSV" button that spits out every artist the station has
 * ever spun. Powers a future "Bands We Spin" page and also gives Sondra a
 * proper record of who's had airtime.
 */
function SpinHistoryCard({ token, onUnauthorized }) {
  const [stats, setStats] = useState(null);
  const [busy, setBusy] = useState(false);
  const [view, setView] = useState("tracks");

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/admin/spin-history/stats`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setStats(res.data);
      } catch (err) {
        if (err?.response?.status === 401) onUnauthorized?.();
      }
    })();
  }, [token, onUnauthorized]);

  const handleDownload = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const res = await axios.get(
        `${API}/admin/spin-history/csv?view=${encodeURIComponent(view)}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: "blob",
        },
      );
      const url = URL.createObjectURL(res.data);
      const today = new Date().toISOString().slice(0, 10);
      const a = document.createElement("a");
      a.href = url;
      a.download = `drr-spin-history-${view}-${today}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Spin history downloaded 🤘");
    } catch (err) {
      if (err?.response?.status === 401) {
        onUnauthorized?.();
        toast.error("Session expired. Log in again.");
      } else {
        toast.error("Download failed — try again");
      }
    } finally {
      setBusy(false);
    }
  };

  const fmt = (n) => (typeof n === "number" ? n.toLocaleString() : "—");

  return (
    <section
      className="px-4 md:px-10 pt-6"
      data-testid="spin-history-card"
    >
      <div className="border-2 border-red-600/70 bg-black/60 p-5 md:p-6 flex flex-col md:flex-row md:items-start gap-5">
        <div className="flex-1 min-w-0">
          <div className="text-[10px] tracking-[0.35em] text-red-500 uppercase mb-1">
            //_SPIN_HISTORY
          </div>
          <h2 className="font-display text-2xl md:text-3xl text-zinc-50 leading-tight tracking-tight">
            EVERY SONG <span className="text-red-500">WE&apos;VE SPUN</span>.
          </h2>
          <p className="text-sm text-zinc-300 mt-2 leading-relaxed max-w-xl">
            Download a CSV of the station&apos;s spin log. Pick the view:
            unique artists, unique tracks (song + artist), or the raw play
            events. Permanent from Aug 14, 2026 forward — older plays were
            auto-trimmed by the recently-played widget before this fix.
          </p>
          {stats && (
            <div className="mt-3 flex flex-wrap gap-4 text-xs tracking-widest uppercase font-mono text-zinc-400">
              <span>
                //_artists:{" "}
                <span className="text-zinc-50" data-testid="spin-stats-artists">
                  {fmt(stats.unique_artists)}
                </span>
              </span>
              <span>
                //_tracks:{" "}
                <span className="text-zinc-50" data-testid="spin-stats-tracks">
                  {fmt(stats.unique_tracks)}
                </span>
              </span>
              <span>
                //_plays:{" "}
                <span className="text-zinc-50" data-testid="spin-stats-plays">
                  {fmt(stats.total_plays)}
                </span>
              </span>
            </div>
          )}
        </div>
        <div className="flex flex-col gap-2 shrink-0">
          <select
            value={view}
            onChange={(e) => setView(e.target.value)}
            className="bg-zinc-950 border border-zinc-800 focus:border-red-600 outline-none px-3 py-2 text-xs tracking-widest uppercase text-zinc-100"
            data-testid="spin-history-view-select"
          >
            <option value="tracks">Songs + Artists</option>
            <option value="artists">Artists Only</option>
            <option value="plays">Full Play Log</option>
          </select>
          <button
            type="button"
            onClick={handleDownload}
            disabled={busy}
            className="bg-red-600 text-white hover:bg-red-500 disabled:opacity-50 disabled:cursor-not-allowed font-display tracking-[0.25em] uppercase text-xs px-5 py-3 flex items-center gap-2 transition-colors"
            data-testid="spin-history-download-btn"
          >
            <Download className="w-4 h-4" strokeWidth={2.5} />
            {busy ? "Building CSV…" : "Download CSV"}
          </button>
        </div>
      </div>
    </section>
  );
}


/**
 * "Send the Top 10 hype email NOW" admin card. The weekly cron auto-sends
 * every Monday 09:00 UTC, but this lets the user preview the email format
 * on demand — and trigger a one-off blast if they want to share the wall
 * mid-week. Touches no idempotency state, so the next Monday email still
 * goes out automatically.
 */
function Top10EmailCard({ token, onUnauthorized }) {
  const [busy, setBusy] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const handleSend = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const res = await axios.post(
        `${API}/admin/send-top10-preview`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const d = res.data || {};
      setLastResult(d);
      if (d.ok) {
        toast.success(
          `Top 10 email sent · ${d.tracks} tracks · ${d.total_hearts} total hearts`
        );
      } else if (d.reason === "no_hearts_yet") {
        toast.error("No tracks hearted yet — nothing to email");
      } else {
        toast.error("Send failed — check email config");
      }
    } catch (err) {
      if (err?.response?.status === 401) {
        onUnauthorized?.();
        toast.error("Session expired. Log in again.");
      } else {
        toast.error("Send failed — try again");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      className="px-4 md:px-10 pt-6"
      data-testid="top10-email-card"
    >
      <div className="border-2 border-red-600 bg-black/60 p-5 md:p-6 flex flex-col md:flex-row md:items-center gap-5">
        <div className="flex-1 min-w-0">
          <div className="text-[10px] tracking-[0.35em] text-red-500 uppercase mb-1 flex items-center gap-2">
            <Heart className="w-3 h-3" fill="currentColor" strokeWidth={0} />
            //_TOP_10_HYPE_DIGEST
          </div>
          <h2 className="font-display text-2xl md:text-3xl text-zinc-50 leading-tight tracking-tight">
            POST IT TO <span className="text-red-500">SOCIALS</span>.
          </h2>
          <p className="text-sm text-zinc-300 mt-2 leading-relaxed max-w-xl">
            Every Monday 9 AM UTC you&apos;ll get a copy-paste-ready hype email with
            this week&apos;s Listener Top 10 — ranks, hearts, and per-track share
            URLs baked in. Click below to send yourself a preview right now.
          </p>
          {lastResult?.ok && (
            <div className="mt-3 text-xs text-zinc-400 tracking-widest uppercase font-mono">
              //_last_send: {lastResult.tracks} tracks · {lastResult.total_hearts} hearts
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={handleSend}
          disabled={busy}
          className="bg-red-600 text-white hover:bg-zinc-50 hover:text-black disabled:opacity-50 disabled:cursor-not-allowed font-display tracking-[0.25em] uppercase text-xs px-5 py-3 flex items-center gap-2 transition-colors shrink-0"
          data-testid="send-top10-preview-btn"
        >
          <Mail className="w-4 h-4" strokeWidth={2.5} />
          {busy ? "Sending…" : "Send Top 10 Preview"}
        </button>
      </div>
    </section>
  );
}


/**
 * Jingle Studio card — type a script, pick a voice, generate an MP3 jingle
 * via ElevenLabs TTS, preview + download it, and keep a library of past
 * jingles. Designed for short station idents (e.g. "Digital Revolution
 * Radio — pure metal, 24/7") that the user uploads to Live365 / Radiojar
 * rotation.
 */
function JingleStudioCard({ token, onUnauthorized }) {
  const MAX_CHARS = 600;
  const [voices, setVoices] = useState([]);
  const [voicesError, setVoicesError] = useState("");
  const [loadingVoices, setLoadingVoices] = useState(true);
  const [name, setName] = useState("");
  const [script, setScript] = useState("");
  const [voiceId, setVoiceId] = useState("");
  const [generating, setGenerating] = useState(false);
  const [latest, setLatest] = useState(null); // most recent generation
  const [library, setLibrary] = useState([]);
  const [libraryLoading, setLibraryLoading] = useState(false);
  const [showLibrary, setShowLibrary] = useState(false);

  // === Delivery presets ===
  // "dj" — punchy defaults tuned for radio idents (low stability, high style
  // + speaker boost). "narrator" — calmer, audiobook-friendly. "custom" —
  // exposes the four sliders so the admin can dial in a signature delivery.
  const PRESETS = {
    dj: { stability: 0.30, similarity_boost: 0.85, style: 0.70, use_speaker_boost: true, label: "Radio DJ" },
    narrator: { stability: 0.75, similarity_boost: 0.75, style: 0.15, use_speaker_boost: false, label: "Narrator" },
  };
  const [preset, setPreset] = useState("dj");
  const [customSettings, setCustomSettings] = useState(PRESETS.dj);

  // === Background riffs ===
  const [riffs, setRiffs] = useState([]);
  const [riffId, setRiffId] = useState("");
  const [riffVolume, setRiffVolume] = useState(0.35);

  const authHeaders = { Authorization: `Bearer ${token}` };

  // Load voices once on mount.
  useEffect(() => {
    let cancelled = false;
    setLoadingVoices(true);
    axios
      .get(`${API}/admin/jingles/voices`, { headers: authHeaders })
      .then((res) => {
        if (cancelled) return;
        const list = res?.data?.voices || [];
        setVoices(list);
        setVoiceId(res?.data?.default_voice_id || list[0]?.voice_id || "");
      })
      .catch((err) => {
        if (cancelled) return;
        if (err?.response?.status === 401) {
          onUnauthorized?.();
        }
        const detail =
          err?.response?.data?.detail ||
          "Couldn't reach ElevenLabs. Check the API key on the server.";
        setVoicesError(detail);
      })
      .finally(() => {
        if (!cancelled) setLoadingVoices(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  // Load riff library once so the "Background Riff" dropdown is populated
  // before the admin opens the picker. Silent on error — the dropdown just
  // shows "None" and admin can upload from the Riff Library card below.
  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/admin/riffs`, { headers: authHeaders })
      .then((res) => {
        if (cancelled) return;
        setRiffs(res?.data?.riffs || []);
      })
      .catch(() => {
        /* silent */
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const loadLibrary = async () => {
    setLibraryLoading(true);
    try {
      const res = await axios.get(`${API}/admin/jingles`, { headers: authHeaders });
      setLibrary(res?.data?.jingles || []);
    } catch (err) {
      if (err?.response?.status === 401) onUnauthorized?.();
      toast.error("Couldn't load jingle library");
    } finally {
      setLibraryLoading(false);
    }
  };

  const handleToggleLibrary = () => {
    const next = !showLibrary;
    setShowLibrary(next);
    if (next && library.length === 0) loadLibrary();
  };

  const handleGenerate = async (e) => {
    e?.preventDefault?.();
    if (generating) return;
    const trimmed = script.trim();
    if (!trimmed) {
      toast.error("Write a script first");
      return;
    }
    if (!voiceId) {
      toast.error("Pick a voice");
      return;
    }
    setGenerating(true);
    // Resolve voice_settings from preset or custom sliders. Only the values
    // the admin actually chose are sent — the backend applies punchy DJ
    // defaults for anything omitted.
    const activeSettings =
      preset === "custom" ? customSettings : PRESETS[preset];
    const payload = {
      name: name.trim() || "Untitled Jingle",
      script: trimmed,
      voice_id: voiceId,
      stability: activeSettings.stability,
      similarity_boost: activeSettings.similarity_boost,
      style: activeSettings.style,
      use_speaker_boost: activeSettings.use_speaker_boost,
    };
    if (riffId) {
      payload.riff_id = riffId;
      payload.riff_volume = riffVolume;
    }
    try {
      const res = await axios.post(
        `${API}/admin/jingles/generate`,
        payload,
        { headers: authHeaders }
      );
      const data = res?.data;
      if (data?.ok) {
        setLatest(data);
        toast.success(
          `Generated · ${data.char_count} chars${riffId ? " + riff" : ""}`
        );
        // Refresh library so the new one shows up if the panel's open.
        if (showLibrary) loadLibrary();
      } else {
        toast.error("Generation failed");
      }
    } catch (err) {
      if (err?.response?.status === 401) onUnauthorized?.();
      const detail = err?.response?.data?.detail || "Generation failed";
      toast.error(detail);
    } finally {
      setGenerating(false);
    }
  };

  const handleDownload = (jingle) => {
    if (!jingle?.audio_url) return;
    const a = document.createElement("a");
    a.href = jingle.audio_url;
    const safe = (jingle.name || "drr-jingle")
      .replace(/[^a-z0-9\-_]/gi, "-")
      .slice(0, 60) || "drr-jingle";
    a.download = `${safe}.mp3`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const handleDelete = async (jingle) => {
    if (!jingle?.id) return;
    if (!window.confirm(`Delete "${jingle.name}"?`)) return;
    try {
      await axios.delete(`${API}/admin/jingles/${jingle.id}`, { headers: authHeaders });
      setLibrary((prev) => prev.filter((j) => j.id !== jingle.id));
      if (latest?.id === jingle.id) setLatest(null);
      toast("Deleted");
    } catch (err) {
      if (err?.response?.status === 401) onUnauthorized?.();
      toast.error("Couldn't delete");
    }
  };

  // Toggle which jingle is shown on the public site (next to the Live365
  // player). Backend enforces single-featured: featuring one auto-unfeatures
  // all others, so we just refresh the library to reflect the new state.
  const handleToggleFeature = async (jingle) => {
    if (!jingle?.id) return;
    const next = !jingle.featured;
    try {
      await axios.patch(
        `${API}/admin/jingles/${jingle.id}/featured`,
        { featured: next },
        { headers: authHeaders }
      );
      setLibrary((prev) =>
        prev.map((j) => ({
          ...j,
          featured: j.id === jingle.id ? next : next ? false : j.featured,
        }))
      );
      toast.success(
        next ? `Now featured on the site · ${jingle.name}` : "Removed from the site"
      );
    } catch (err) {
      if (err?.response?.status === 401) onUnauthorized?.();
      toast.error("Couldn't update featured state");
    }
  };

  const chars = script.length;
  const charsTone =
    chars > MAX_CHARS
      ? "text-red-500"
      : chars > MAX_CHARS * 0.85
      ? "text-yellow-400"
      : "text-zinc-500";

  return (
    <section className="px-4 md:px-10 pt-6" data-testid="jingle-studio-card">
      <div className="border-2 border-red-600 bg-black/60 p-5 md:p-6">
        <div className="flex items-center gap-3 mb-4">
          <Mic2 className="w-5 h-5 text-red-500" />
          <div className="text-[10px] tracking-[0.35em] text-red-500 uppercase">
            //_JINGLE_STUDIO
          </div>
          <span className="text-[10px] text-zinc-500 tracking-widest uppercase ml-auto">
            Powered by ElevenLabs
          </span>
        </div>
        <h2 className="font-display text-2xl md:text-3xl text-zinc-50 leading-tight tracking-tight mb-1">
          GENERATE A <span className="text-red-600">JINGLE</span>.
        </h2>
        <p className="text-sm text-zinc-400 leading-relaxed max-w-2xl mb-5">
          Type a station ident or shout-out, pick a voice, and get back an MP3
          ready to drop into your Live365 / Radiojar rotation. Keep it short
          and punchy — 8 to 15 seconds is the sweet spot for radio idents.
        </p>

        {voicesError && (
          <div
            className="mb-4 border border-red-500 bg-red-500/10 text-red-400 text-xs tracking-widest uppercase p-3"
            data-testid="jingle-studio-voices-error"
          >
            //_error: {voicesError}
          </div>
        )}

        <form onSubmit={handleGenerate} className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <label className="flex flex-col gap-1.5">
              <span className="text-[10px] tracking-[0.3em] text-zinc-500 uppercase">
                //_jingle_name
              </span>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value.slice(0, 80))}
                placeholder="e.g. Station ID Hour 1"
                className="bg-zinc-950 border border-zinc-800 focus:border-red-600 focus:outline-none px-3 py-2 text-sm text-zinc-100"
                data-testid="jingle-studio-name"
              />
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-[10px] tracking-[0.3em] text-zinc-500 uppercase">
                //_voice
              </span>
              <select
                value={voiceId}
                onChange={(e) => setVoiceId(e.target.value)}
                disabled={loadingVoices || voices.length === 0}
                className="bg-zinc-950 border border-zinc-800 focus:border-red-600 focus:outline-none px-3 py-2 text-sm text-zinc-100 disabled:opacity-50"
                data-testid="jingle-studio-voice"
              >
                {loadingVoices && <option>//_loading_voices…</option>}
                {!loadingVoices && voices.length === 0 && (
                  <option value="">//_no_voices_available</option>
                )}
                {voices.map((v) => (
                  <option key={v.voice_id} value={v.voice_id}>
                    {v.curated ? "★ " : ""}
                    {v.label}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <label className="flex flex-col gap-1.5">
            <span className="text-[10px] tracking-[0.3em] text-zinc-500 uppercase flex items-center justify-between">
              <span>//_script</span>
              <span className={`tracking-widest ${charsTone}`}>
                {chars}/{MAX_CHARS}
              </span>
            </span>
            <textarea
              value={script}
              onChange={(e) => setScript(e.target.value.slice(0, MAX_CHARS))}
              rows={4}
              placeholder={`e.g. "You're listening to Digital Revolution Radio. Pure metal. 24/7. Crank it up."`}
              className="bg-zinc-950 border border-zinc-800 focus:border-red-600 focus:outline-none px-3 py-2 text-sm text-zinc-100 font-mono leading-relaxed resize-y"
              data-testid="jingle-studio-script"
            />
          </label>

          {/* === DELIVERY PRESET === */}
          <div
            className="border border-zinc-800 bg-zinc-950 p-3"
            data-testid="jingle-studio-delivery"
          >
            <div className="text-[10px] tracking-[0.3em] text-zinc-500 uppercase mb-2 flex items-center justify-between gap-2 flex-wrap">
              <span>//_delivery_energy</span>
              <span className="text-zinc-600">
                punch tuning · applies to any voice
              </span>
            </div>
            <div className="grid grid-cols-3 gap-0 border border-zinc-800" data-testid="jingle-studio-preset-row">
              {[
                { key: "dj", label: "Radio DJ", sub: "MAX PUNCH" },
                { key: "narrator", label: "Narrator", sub: "CALM" },
                { key: "custom", label: "Custom", sub: "DIAL IT IN" },
              ].map((p, i) => (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => setPreset(p.key)}
                  className={`px-3 py-2 text-xs font-display tracking-widest uppercase border-zinc-800 transition-colors ${
                    i > 0 ? "border-l" : ""
                  } ${
                    preset === p.key
                      ? "bg-red-600 text-white"
                      : "bg-black text-zinc-400 hover:text-red-500"
                  }`}
                  data-testid={`jingle-studio-preset-${p.key}`}
                >
                  <div>{p.label}</div>
                  <div className="text-[9px] tracking-[0.2em] mt-0.5 opacity-70">
                    {p.sub}
                  </div>
                </button>
              ))}
            </div>
            {preset === "custom" && (
              <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="jingle-studio-custom-sliders">
                <VoiceSlider
                  label="Stability"
                  hint="Low = more emotion"
                  value={customSettings.stability}
                  onChange={(v) => setCustomSettings((s) => ({ ...s, stability: v }))}
                  testId="jingle-slider-stability"
                />
                <VoiceSlider
                  label="Similarity"
                  hint="Voice character preserved"
                  value={customSettings.similarity_boost}
                  onChange={(v) => setCustomSettings((s) => ({ ...s, similarity_boost: v }))}
                  testId="jingle-slider-similarity"
                />
                <VoiceSlider
                  label="Style / Drama"
                  hint="Higher = more punch"
                  value={customSettings.style}
                  onChange={(v) => setCustomSettings((s) => ({ ...s, style: v }))}
                  testId="jingle-slider-style"
                />
                <label className="flex items-center justify-between gap-3 bg-black border border-zinc-800 px-3 py-2">
                  <div className="min-w-0">
                    <div className="text-[10px] tracking-[0.25em] text-zinc-400 uppercase">
                      Speaker Boost
                    </div>
                    <div className="text-[9px] tracking-widest text-zinc-600 uppercase mt-0.5">
                      more presence + loudness
                    </div>
                  </div>
                  <input
                    type="checkbox"
                    checked={!!customSettings.use_speaker_boost}
                    onChange={(e) =>
                      setCustomSettings((s) => ({
                        ...s,
                        use_speaker_boost: e.target.checked,
                      }))
                    }
                    className="w-4 h-4 accent-red-600"
                    data-testid="jingle-slider-boost"
                  />
                </label>
              </div>
            )}
          </div>

          {/* === BACKGROUND RIFF === */}
          <div
            className="border border-zinc-800 bg-zinc-950 p-3"
            data-testid="jingle-studio-riff"
          >
            <div className="text-[10px] tracking-[0.3em] text-zinc-500 uppercase mb-2 flex items-center justify-between gap-2 flex-wrap">
              <span>//_background_riff</span>
              <span className="text-zinc-600">
                {riffs.length === 0
                  ? "upload riffs in the library below"
                  : `${riffs.length} available · looped under voice`}
              </span>
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              <select
                value={riffId}
                onChange={(e) => setRiffId(e.target.value)}
                disabled={riffs.length === 0}
                className="bg-black border border-zinc-800 focus:border-red-600 focus:outline-none px-3 py-2 text-sm text-zinc-100 disabled:opacity-50 font-mono"
                data-testid="jingle-studio-riff-select"
              >
                <option value="">//_NONE · voice_only</option>
                {riffs.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name}
                  </option>
                ))}
              </select>
              <div className="bg-black border border-zinc-800 px-3 py-2 flex items-center gap-3">
                <span className="text-[10px] tracking-[0.25em] text-zinc-400 uppercase shrink-0">
                  Riff Vol
                </span>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.05"
                  value={riffVolume}
                  onChange={(e) => setRiffVolume(parseFloat(e.target.value))}
                  disabled={!riffId}
                  className="flex-1 accent-red-600 disabled:opacity-40"
                  data-testid="jingle-studio-riff-volume"
                />
                <span className="text-[10px] tracking-widest text-zinc-500 font-mono w-8 text-right">
                  {Math.round(riffVolume * 100)}%
                </span>
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <button
              type="submit"
              disabled={generating || !script.trim() || !voiceId}
              className="bg-red-600 text-white hover:bg-zinc-50 hover:text-black disabled:opacity-50 disabled:cursor-not-allowed font-display tracking-[0.25em] uppercase text-xs px-5 py-3 flex items-center gap-2 transition-colors"
              data-testid="jingle-studio-generate"
            >
              <Mic2 className="w-4 h-4" strokeWidth={2.5} />
              {generating ? "Generating…" : "Generate Jingle"}
            </button>
            <button
              type="button"
              onClick={handleToggleLibrary}
              className="text-xs tracking-[0.25em] uppercase text-zinc-400 hover:text-red-500 transition-colors flex items-center gap-2"
              data-testid="jingle-studio-library-toggle"
            >
              {showLibrary ? "Hide Library" : "Open Library"}
              <span className="text-zinc-600">{showLibrary ? "▴" : "▾"}</span>
            </button>
          </div>
        </form>

        {/* Latest preview */}
        {latest?.audio_url && (
          <div
            className="mt-6 border border-zinc-800 bg-zinc-950 p-4"
            data-testid="jingle-studio-latest"
          >
            <div className="flex items-center justify-between mb-2 gap-3">
              <div className="min-w-0">
                <div className="text-[10px] tracking-[0.3em] text-red-500 uppercase">
                  //_just_generated
                </div>
                <div className="text-sm text-zinc-100 font-mono truncate mt-1">
                  {latest.name}
                </div>
                <div className="text-[10px] text-zinc-500 tracking-widest uppercase mt-0.5">
                  {latest.voice_label} · {latest.char_count} chars
                </div>
              </div>
              <button
                type="button"
                onClick={() => handleDownload(latest)}
                className="shrink-0 border border-red-600 text-red-500 hover:bg-red-600 hover:text-white px-3 py-2 text-[10px] tracking-[0.25em] uppercase font-display flex items-center gap-2 transition-colors"
                data-testid="jingle-studio-latest-download"
              >
                <Download className="w-3.5 h-3.5" />
                Download MP3
              </button>
            </div>
            <audio
              src={latest.audio_url}
              controls
              className="w-full mt-2"
              data-testid="jingle-studio-latest-audio"
            ></audio>
          </div>
        )}

        {/* Library list */}
        {showLibrary && (
          <div
            className="mt-5 border border-zinc-800 bg-zinc-950 p-4"
            data-testid="jingle-studio-library"
          >
            <div className="text-[10px] tracking-[0.3em] text-zinc-500 uppercase mb-3 flex items-center justify-between">
              <span>//_library</span>
              <button
                type="button"
                onClick={loadLibrary}
                className="text-zinc-600 hover:text-red-500 tracking-widest"
                data-testid="jingle-studio-library-refresh"
              >
                refresh
              </button>
            </div>
            {libraryLoading && (
              <div className="text-xs text-zinc-500 tracking-widest uppercase">
                //_loading…
              </div>
            )}
            {!libraryLoading && library.length === 0 && (
              <div className="text-xs text-zinc-500 tracking-widest uppercase">
                //_no_jingles_yet · generate one above
              </div>
            )}
            <ul className="space-y-3">
              {library.map((j) => (
                <li
                  key={j.id}
                  className="border border-zinc-800 bg-black/40 p-3 flex flex-col gap-2"
                  data-testid="jingle-studio-library-item"
                >
                  <div className="flex items-start justify-between gap-3 min-w-0">
                    <div className="min-w-0">
                      <div className="text-sm text-zinc-100 font-mono truncate flex items-center gap-2">
                        {j.name}
                        {j.featured && (
                          <span
                            className="text-[9px] tracking-[0.3em] uppercase text-red-500 border border-red-600 px-1.5 py-0.5"
                            data-testid="jingle-studio-library-featured-badge"
                          >
                            featured
                          </span>
                        )}
                      </div>
                      <div className="text-[10px] text-zinc-500 tracking-widest uppercase mt-0.5">
                        {j.voice_label} · {j.char_count} chars ·{" "}
                        {new Date(j.created_at).toLocaleString()}
                      </div>
                      {j.script && (
                        <p className="text-[11px] text-zinc-400 mt-1 line-clamp-2 italic">
                          &ldquo;{j.script}&rdquo;
                        </p>
                      )}
                    </div>
                    <div className="shrink-0 flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => handleToggleFeature(j)}
                        title={
                          j.featured
                            ? "Currently featured on the site · click to remove"
                            : "Feature on the site (replaces any current pick)"
                        }
                        aria-label={j.featured ? `Unfeature ${j.name}` : `Feature ${j.name}`}
                        aria-pressed={j.featured}
                        className={`w-8 h-8 flex items-center justify-center border transition-colors ${
                          j.featured
                            ? "border-red-600 bg-red-600/15 text-red-500"
                            : "border-zinc-700 text-zinc-300 hover:text-red-500 hover:border-red-600"
                        }`}
                        data-testid="jingle-studio-library-feature"
                      >
                        <Star
                          className="w-3.5 h-3.5"
                          strokeWidth={2.2}
                          fill={j.featured ? "currentColor" : "none"}
                        />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDownload(j)}
                        title="Download MP3"
                        aria-label={`Download ${j.name}`}
                        className="w-8 h-8 flex items-center justify-center border border-zinc-700 text-zinc-300 hover:text-red-500 hover:border-red-600 transition-colors"
                        data-testid="jingle-studio-library-download"
                      >
                        <Download className="w-3.5 h-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(j)}
                        title="Delete jingle"
                        aria-label={`Delete ${j.name}`}
                        className="w-8 h-8 flex items-center justify-center border border-zinc-700 text-zinc-300 hover:text-red-500 hover:border-red-600 transition-colors"
                        data-testid="jingle-studio-library-delete"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                  {j.audio_url && (
                    <audio
                      src={j.audio_url}
                      controls
                      className="w-full"
                      data-testid="jingle-studio-library-audio"
                    />
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </section>
  );
}



/**
 * Chat Stickers admin card — paste image URLs (Giphy / Tenor / Imgur /
 * direct) that listeners in /chat can drop into the conversation with one
 * click. The chat picker auto-sends the URL as a `[img]…[/img]` message so
 * the existing BBCode renderer takes care of the actual image display.
 */
function ChatStickersCard({ token, onUnauthorized }) {
  const [stickers, setStickers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [saving, setSaving] = useState(false);
  const [sortMode, setSortMode] = useState("newest"); // "newest" | "most-used"

  const authHeaders = { Authorization: `Bearer ${token}` };

  const loadStickers = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/chat-stickers`, { headers: authHeaders });
      setStickers(res?.data?.stickers || []);
    } catch (err) {
      if (err?.response?.status === 401) onUnauthorized?.();
      toast.error("Couldn't load stickers");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStickers();
  }, [token]);

  const handleAdd = async (e) => {
    e?.preventDefault?.();
    if (saving) return;
    const url = imageUrl.trim();
    if (!url) {
      toast.error("Paste an image URL first");
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(
        `${API}/admin/chat-stickers`,
        { name: name.trim(), image_url: url },
        { headers: authHeaders }
      );
      if (res?.data?.ok) {
        setStickers((prev) => [
          {
            id: res.data.id,
            name: res.data.name,
            image_url: res.data.image_url,
            created_at: res.data.created_at,
          },
          ...prev,
        ]);
        setName("");
        setImageUrl("");
        toast.success("Sticker added");
      }
    } catch (err) {
      if (err?.response?.status === 401) onUnauthorized?.();
      const detail = err?.response?.data?.detail || "Couldn't add sticker";
      toast.error(detail);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (sticker) => {
    if (!sticker?.id) return;
    if (!window.confirm(`Remove "${sticker.name}" from chat?`)) return;
    try {
      await axios.delete(`${API}/admin/chat-stickers/${sticker.id}`, {
        headers: authHeaders,
      });
      setStickers((prev) => prev.filter((s) => s.id !== sticker.id));
      toast("Removed");
    } catch (err) {
      if (err?.response?.status === 401) onUnauthorized?.();
      toast.error("Couldn't remove");
    }
  };

  return (
    <section className="px-4 md:px-10 pt-6" data-testid="chat-stickers-card">
      <div className="border-2 border-red-600 bg-black/60 p-5 md:p-6">
        <div className="flex items-center gap-3 mb-4">
          <ImageIcon className="w-5 h-5 text-red-500" />
          <div className="text-[10px] tracking-[0.35em] text-red-500 uppercase">
            //_CHAT_STICKERS
          </div>
          <span className="text-[10px] text-zinc-500 tracking-widest uppercase ml-auto flex items-center gap-2">
            <span>
              {stickers.length} live
              {stickers.length > 0 && (
                <>
                  {" · "}
                  {stickers.reduce((acc, s) => acc + (s.use_count || 0), 0)} uses
                </>
              )}
            </span>
            {stickers.length > 1 && (
              <button
                type="button"
                onClick={() =>
                  setSortMode((m) => (m === "newest" ? "most-used" : "newest"))
                }
                className="text-zinc-400 hover:text-red-500 tracking-widest border border-zinc-800 hover:border-red-600 px-2 py-0.5 transition-colors"
                data-testid="chat-stickers-sort"
                title="Toggle sort order"
              >
                sort: {sortMode}
              </button>
            )}
            <button
              type="button"
              onClick={loadStickers}
              className="text-zinc-400 hover:text-red-500 tracking-widest underline-offset-2 hover:underline"
              data-testid="chat-stickers-refresh"
            >
              refresh
            </button>
          </span>
        </div>
        <h2 className="font-display text-2xl md:text-3xl text-zinc-50 leading-tight tracking-tight mb-1">
          PASTE A <span className="text-red-600">FUNNY PIC</span>.
        </h2>
        <p className="text-sm text-zinc-400 leading-relaxed max-w-2xl mb-5">
          Paste Giphy / Tenor / Imgur / any direct image URL — and it shows
          up in the chat sticker picker for every listener. They tap it,
          it auto-sends as an inline pic. Headbangers, meme reactions, metal
          icons — whatever you want in their toolkit.
        </p>

        <form
          onSubmit={handleAdd}
          className="grid md:grid-cols-[1fr_2fr_auto] gap-3 items-end mb-5"
        >
          <label className="flex flex-col gap-1.5">
            <span className="text-[10px] tracking-[0.3em] text-zinc-500 uppercase">
              //_label
            </span>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value.slice(0, 60))}
              placeholder="Headbanger"
              className="bg-zinc-950 border border-zinc-800 focus:border-red-600 focus:outline-none px-3 py-2 text-sm text-zinc-100"
              data-testid="chat-stickers-name"
            />
          </label>
          <label className="flex flex-col gap-1.5">
            <span className="text-[10px] tracking-[0.3em] text-zinc-500 uppercase">
              //_image_url
            </span>
            <input
              type="url"
              value={imageUrl}
              onChange={(e) => setImageUrl(e.target.value.slice(0, 1024))}
              placeholder="https://media.giphy.com/media/.../giphy.gif"
              className="bg-zinc-950 border border-zinc-800 focus:border-red-600 focus:outline-none px-3 py-2 text-sm text-zinc-100 font-mono"
              data-testid="chat-stickers-url"
            />
          </label>
          <button
            type="submit"
            disabled={saving || !imageUrl.trim()}
            className="bg-red-600 text-white hover:bg-zinc-50 hover:text-black disabled:opacity-50 disabled:cursor-not-allowed font-display tracking-[0.25em] uppercase text-xs px-5 py-2.5 flex items-center gap-2 transition-colors"
            data-testid="chat-stickers-add"
          >
            <Plus className="w-4 h-4" strokeWidth={2.5} />
            {saving ? "Adding…" : "Add"}
          </button>
        </form>

        <div
          className="border border-zinc-800 bg-zinc-950 p-3"
          data-testid="chat-stickers-list"
        >
          {loading && (
            <div className="text-xs text-zinc-500 tracking-widest uppercase">
              //_loading…
            </div>
          )}
          {!loading && stickers.length === 0 && (
            <div className="text-xs text-zinc-500 tracking-widest uppercase">
              //_no_stickers_yet · paste one above
            </div>
          )}
          {!loading && stickers.length > 0 && (
            <ul
              className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-3"
            >
              {[...stickers]
                .sort((a, b) => {
                  if (sortMode === "most-used") {
                    return (b.use_count || 0) - (a.use_count || 0);
                  }
                  // "newest" — backend already returns newest-first; preserve that
                  // by sorting on created_at ISO desc when present.
                  return (b.created_at || "").localeCompare(a.created_at || "");
                })
                .map((s) => (
                <li
                  key={s.id}
                  className="group relative border border-zinc-800 bg-black overflow-hidden"
                  data-testid="chat-stickers-item"
                  data-use-count={s.use_count || 0}
                >
                  <img
                    src={s.image_url}
                    alt={s.name}
                    loading="lazy"
                    className="w-full aspect-square object-cover"
                    onError={(e) => {
                      e.currentTarget.style.opacity = "0.3";
                      e.currentTarget.title = "Image failed to load";
                    }}
                  />
                  {/* Usage badge — top-left so it doesn't fight the delete
                      button on the top-right. Only shows when > 0 so fresh
                      stickers don't get tagged with an awkward "× 0". */}
                  {(s.use_count || 0) > 0 && (
                    <div
                      className="absolute top-1 left-1 bg-red-600/90 text-white text-[10px] tracking-widest uppercase px-1.5 py-0.5 font-display"
                      title={`Used ${s.use_count} time${s.use_count === 1 ? "" : "s"} in chat`}
                      data-testid="chat-stickers-item-usage"
                    >
                      × {s.use_count}
                    </div>
                  )}
                  <div className="absolute inset-x-0 bottom-0 bg-black/80 px-2 py-1 text-[10px] text-zinc-300 truncate uppercase tracking-widest">
                    {s.name}
                  </div>
                  <button
                    type="button"
                    onClick={() => handleDelete(s)}
                    title={`Remove ${s.name}`}
                    aria-label={`Remove ${s.name}`}
                    className="absolute top-1 right-1 w-7 h-7 flex items-center justify-center bg-black/70 text-zinc-300 hover:bg-red-600 hover:text-white opacity-0 group-hover:opacity-100 transition-opacity"
                    data-testid="chat-stickers-item-delete"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </section>
  );
}



function LoginForm({ onLogin }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { username, password });
      onLogin(res.data.token);
      toast.success(`Welcome back, ${res.data.username}.`);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Login failed";
      toast.error(typeof msg === "string" ? msg : "Login failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-[calc(100vh-80px)] px-4">
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-md border-2 border-zinc-700 bg-black p-8 flex flex-col gap-5"
        data-testid="admin-login-form"
      >
        <div>
          <div className="text-xs tracking-[0.4em] text-zinc-300 uppercase mb-2">
            //_RESTRICTED_AREA
          </div>
          <h1 className="font-display text-4xl text-zinc-50 leading-none">
            LOG <span className="text-red-600">IN</span>.
          </h1>
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-[10px] tracking-[0.3em] text-zinc-300 uppercase">
            Username
          </label>
          <input
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            required
            className="bg-zinc-950 border border-zinc-700 px-3 py-3 text-sm text-zinc-100 outline-none focus:border-red-600 font-mono tracking-wide"
            data-testid="admin-username-input"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-[10px] tracking-[0.3em] text-zinc-300 uppercase">
            Password
          </label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            className="bg-zinc-950 border border-zinc-700 px-3 py-3 text-sm text-zinc-100 outline-none focus:border-red-600 font-mono tracking-wide"
            data-testid="admin-password-input"
          />
        </div>
        <button
          type="submit"
          disabled={submitting}
          className="bg-red-600 hover:bg-white hover:text-black disabled:opacity-50 text-white font-display tracking-widest uppercase px-6 py-3 transition-colors border border-zinc-700"
          data-testid="admin-login-submit"
        >
          {submitting ? "Verifying..." : "Enter Control Panel"}
        </button>
        <p className="text-[10px] text-zinc-600 tracking-widest uppercase">
          //_for_authorized_personnel_only
        </p>
      </form>
    </div>
  );
}

const EMPTY_BAND = {
  name: "",
  photo_url: "",
  blurb: "",
  music_link: "",
  featuring_dj: "",
  featuring_show: "",
};

function BandsManager({ token, onUnauthorized }) {
  const [bands, setBands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // band id being edited, or "new"
  const [form, setForm] = useState(EMPTY_BAND);
  const [saving, setSaving] = useState(false);
  const formRef = useRef(null);

  const authHeader = { Authorization: `Bearer ${token}` };

  const load = async () => {
    try {
      const res = await axios.get(`${API}/featured-bands`);
      setBands(res.data || []);
    } catch (e) {
      toast.error("Failed to load bands");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/featured-bands`);
        if (!cancelled) {
          setBands(res.data || []);
          setLoading(false);
        }
      } catch (e) {
        if (!cancelled) {
          toast.error("Failed to load bands");
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Scroll the edit form into view whenever we open it. Without this,
  // clicking "Edit" on a band card scrolled below the fold opens the form
  // at the top of the page — feels like nothing happened to the user.
  useEffect(() => {
    if (editing && formRef.current) {
      formRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [editing]);

  const startNew = () => {
    setEditing("new");
    setForm(EMPTY_BAND);
  };

  const startEdit = (band) => {
    setEditing(band.id);
    setForm({
      name: band.name,
      photo_url: band.photo_url,
      blurb: band.blurb,
      music_link: band.music_link || "",
      featuring_dj: band.featuring_dj || "",
      featuring_show: band.featuring_show || "",
    });
  };

  const cancelEdit = () => {
    setEditing(null);
    setForm(EMPTY_BAND);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (saving) return;
    if (!form.name.trim() || !form.photo_url.trim() || !form.blurb.trim()) {
      toast.error("Name, photo URL, and blurb are required");
      return;
    }
    setSaving(true);
    try {
      if (editing === "new") {
        await axios.post(`${API}/featured-bands`, form, { headers: authHeader });
        toast.success("Band added 🤘");
      } else {
        await axios.put(`${API}/featured-bands/${editing}`, form, {
          headers: authHeader,
        });
        toast.success("Band updated");
      }
      cancelEdit();
      load();
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Session expired — please log in again");
        onUnauthorized();
      } else {
        const msg = err?.response?.data?.detail || "Save failed";
        toast.error(typeof msg === "string" ? msg : "Save failed");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (band) => {
    if (!window.confirm(`Delete "${band.name}"? This can't be undone.`)) return;
    try {
      await axios.delete(`${API}/featured-bands/${band.id}`, {
        headers: authHeader,
      });
      toast.success("Band deleted");
      load();
    } catch (err) {
      if (err?.response?.status === 401) {
        onUnauthorized();
      } else {
        toast.error("Delete failed");
      }
    }
  };

  return (
    <div className="px-4 md:px-10 py-8">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="text-xs tracking-[0.4em] text-zinc-300 uppercase mb-2">
            //_FEATURED_BANDS
          </div>
          <h2 className="font-display text-3xl md:text-5xl text-zinc-50 leading-none">
            BANDS WE&apos;RE <span className="text-red-600">BLASTING</span>.
          </h2>
          <p className="text-xs text-zinc-300 mt-2 tracking-widest uppercase">
            //_{bands.length} active · public on homepage
          </p>
        </div>
        {!editing && (
          <button
            onClick={startNew}
            className="bg-red-600 hover:bg-white hover:text-black text-white font-display tracking-widest uppercase px-5 py-3 transition-colors border border-zinc-700 inline-flex items-center gap-2"
            data-testid="admin-add-band-btn"
          >
            <Plus className="w-4 h-4" /> Add Band
          </button>
        )}
      </div>

      {editing && (
        <form
          ref={formRef}
          onSubmit={handleSave}
          className="mb-8 border-2 border-red-600 bg-black p-6 flex flex-col gap-4"
          data-testid="admin-band-form"
        >
          <div className="flex items-center justify-between">
            <h3 className="font-display text-2xl text-zinc-50 tracking-tight">
              {editing === "new" ? "NEW BAND" : "EDIT BAND"}
            </h3>
            <button
              type="button"
              onClick={cancelEdit}
              className="text-zinc-300 hover:text-red-500 text-xs tracking-widest uppercase inline-flex items-center gap-1"
              data-testid="admin-cancel-edit"
            >
              <X className="w-3 h-3" /> Cancel
            </button>
          </div>
          <Field label="Band Name *" testid="form-name">
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
              maxLength={120}
              className="bg-zinc-950 border border-zinc-700 px-3 py-2 text-sm text-zinc-100 outline-none focus:border-red-600 font-mono"
              data-testid="form-name-input"
            />
          </Field>
          <Field label="Photo URL * (full https://... link to an image)" testid="form-photo">
            <input
              type="url"
              value={form.photo_url}
              onChange={(e) => setForm({ ...form, photo_url: e.target.value })}
              required
              maxLength={500}
              placeholder="https://example.com/band-photo.jpg"
              className="bg-zinc-950 border border-zinc-700 px-3 py-2 text-sm text-zinc-100 outline-none focus:border-red-600 font-mono"
              data-testid="form-photo-input"
            />
            {form.photo_url && (
              <img
                src={form.photo_url}
                alt="preview"
                className="mt-2 max-h-32 border border-zinc-800"
                onError={(e) => (e.currentTarget.style.display = "none")}
              />
            )}
          </Field>
          <Field label="Blurb * (short description)" testid="form-blurb">
            <textarea
              value={form.blurb}
              onChange={(e) => setForm({ ...form, blurb: e.target.value })}
              required
              maxLength={1500}
              rows={4}
              placeholder="What makes this band worth featuring?"
              className="bg-zinc-950 border border-zinc-700 px-3 py-2 text-sm text-zinc-100 outline-none focus:border-red-600 font-mono resize-y"
              data-testid="form-blurb-input"
            />
          </Field>
          <Field label="Music Link (Spotify, Bandcamp, YouTube, etc.)" testid="form-music">
            <input
              type="url"
              value={form.music_link}
              onChange={(e) => setForm({ ...form, music_link: e.target.value })}
              maxLength={500}
              placeholder="https://bandcamp.com/..."
              className="bg-zinc-950 border border-zinc-700 px-3 py-2 text-sm text-zinc-100 outline-none focus:border-red-600 font-mono"
              data-testid="form-music-input"
            />
          </Field>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Featuring DJ" testid="form-dj">
              <input
                type="text"
                value={form.featuring_dj}
                onChange={(e) => setForm({ ...form, featuring_dj: e.target.value })}
                maxLength={120}
                placeholder="e.g. Doc Rock"
                className="bg-zinc-950 border border-zinc-700 px-3 py-2 text-sm text-zinc-100 outline-none focus:border-red-600 font-mono"
                data-testid="form-dj-input"
              />
            </Field>
            <Field label="Featuring Show" testid="form-show">
              <input
                type="text"
                value={form.featuring_show}
                onChange={(e) => setForm({ ...form, featuring_show: e.target.value })}
                maxLength={120}
                placeholder="e.g. Metal Shop 101"
                className="bg-zinc-950 border border-zinc-700 px-3 py-2 text-sm text-zinc-100 outline-none focus:border-red-600 font-mono"
                data-testid="form-show-input"
              />
            </Field>
          </div>
          <button
            type="submit"
            disabled={saving}
            className="self-start bg-red-600 hover:bg-white hover:text-black disabled:opacity-50 text-white font-display tracking-widest uppercase px-6 py-3 transition-colors border border-zinc-700 inline-flex items-center gap-2"
            data-testid="admin-save-band"
          >
            <Save className="w-4 h-4" /> {saving ? "Saving..." : "Save Band"}
          </button>
        </form>
      )}

      {loading ? (
        <div className="text-xs tracking-widest uppercase text-zinc-500">
          //_loading_bands...
        </div>
      ) : bands.length === 0 ? (
        <div className="border-2 border-dashed border-zinc-700 p-10 text-center">
          <div className="font-display text-2xl text-zinc-50 mb-2">
            NO BANDS YET.
          </div>
          <p className="text-sm text-zinc-300">
            Click &quot;Add Band&quot; to feature your first artist.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-0 border-l border-t border-zinc-800">
          {bands.map((band) => (
            <div
              key={band.id}
              className="border-r border-b border-zinc-800 bg-black p-4 flex flex-col gap-3"
              data-testid={`admin-band-row-${band.id}`}
            >
              <div className="aspect-video bg-zinc-900 overflow-hidden border border-zinc-800">
                <img
                  src={band.photo_url}
                  alt={band.name}
                  className="w-full h-full object-cover"
                  onError={(e) => (e.currentTarget.style.display = "none")}
                />
              </div>
              <div>
                <div className="font-display text-xl text-zinc-50 tracking-tight leading-tight">
                  {band.name}
                </div>
                {(band.featuring_dj || band.featuring_show) && (
                  <div className="text-[10px] tracking-widest uppercase text-zinc-300 mt-1">
                    {band.featuring_dj}
                    {band.featuring_dj && band.featuring_show ? " · " : ""}
                    {band.featuring_show}
                  </div>
                )}
              </div>
              <p className="text-xs text-zinc-200 leading-relaxed line-clamp-4">
                {band.blurb}
              </p>
              <div className="flex gap-2 mt-auto pt-2 border-t border-zinc-800">
                <button
                  onClick={() => startEdit(band)}
                  className="flex-1 text-xs tracking-widest uppercase border border-zinc-700 px-3 py-2 hover:bg-zinc-100 hover:text-black transition-colors inline-flex items-center justify-center gap-1"
                  data-testid={`admin-edit-${band.id}`}
                >
                  <Edit3 className="w-3 h-3" /> Edit
                </button>
                <button
                  onClick={() => handleDelete(band)}
                  className="text-xs tracking-widest uppercase border border-red-700 text-red-500 px-3 py-2 hover:bg-red-600 hover:text-white transition-colors inline-flex items-center justify-center gap-1"
                  data-testid={`admin-delete-${band.id}`}
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Field({ label, children, testid }) {
  return (
    <label className="flex flex-col gap-1" data-testid={testid}>
      <span className="text-[10px] tracking-[0.3em] text-zinc-300 uppercase">
        {label}
      </span>
      {children}
    </label>
  );
}

/**
 * VoiceSlider — labelled slider row for the JingleStudioCard custom-preset
 * expander. Value is 0-1 float; we display a %  for a fingertip-friendly
 * read.
 */
function VoiceSlider({ label, hint, value, onChange, testId }) {
  const pct = Math.round((value ?? 0) * 100);
  return (
    <label className="bg-black border border-zinc-800 px-3 py-2 flex items-center gap-3">
      <div className="min-w-0 flex-1">
        <div className="text-[10px] tracking-[0.25em] text-zinc-400 uppercase">
          {label}
        </div>
        <div className="text-[9px] tracking-widest text-zinc-600 uppercase mt-0.5">
          {hint}
        </div>
      </div>
      <input
        type="range"
        min="0"
        max="1"
        step="0.05"
        value={value ?? 0}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        className="w-28 accent-red-600"
        data-testid={testId}
      />
      <span className="text-[10px] tracking-widest text-zinc-500 font-mono w-8 text-right">
        {pct}%
      </span>
    </label>
  );
}

/**
 * RiffLibraryCard — admin panel section for uploading background riffs
 * (short instrumental loops) used by the Jingle Studio's "Background Riff"
 * picker. Riffs get re-encoded to 128kbps MP3 44.1kHz by the backend on
 * upload so all clips mix identically against the ElevenLabs voice output.
 *
 * Persisted in Mongo (base64) same as jingles; capped at 5MB per file.
 */
function RiffLibraryCard({ token, onUnauthorized }) {
  const [riffs, setRiffs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [name, setName] = useState("");
  const [file, setFile] = useState(null);
  const fileInputRef = useRef(null);

  const authHeaders = { Authorization: `Bearer ${token}` };

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/riffs`, { headers: authHeaders });
      setRiffs(res?.data?.riffs || []);
    } catch (err) {
      if (err?.response?.status === 401) onUnauthorized?.();
      toast.error("Couldn't load riff library");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [token]);

  const handleUpload = async (e) => {
    e?.preventDefault?.();
    if (uploading) return;
    const cleanName = name.trim();
    if (!cleanName) {
      toast.error("Give the riff a name");
      return;
    }
    if (!file) {
      toast.error("Pick an audio file");
      return;
    }
    setUploading(true);
    const fd = new FormData();
    fd.append("name", cleanName);
    fd.append("file", file);
    try {
      await axios.post(`${API}/admin/riffs`, fd, {
        headers: {
          ...authHeaders,
          "Content-Type": "multipart/form-data",
        },
      });
      toast.success(`Uploaded · ${cleanName}`);
      setName("");
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      await load();
    } catch (err) {
      if (err?.response?.status === 401) onUnauthorized?.();
      const detail = err?.response?.data?.detail || "Upload failed";
      toast.error(detail);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (riff) => {
    if (!riff?.id) return;
    if (!window.confirm(`Delete riff "${riff.name}"?`)) return;
    try {
      await axios.delete(`${API}/admin/riffs/${riff.id}`, { headers: authHeaders });
      setRiffs((prev) => prev.filter((r) => r.id !== riff.id));
      toast("Deleted");
    } catch (err) {
      if (err?.response?.status === 401) onUnauthorized?.();
      toast.error("Couldn't delete riff");
    }
  };

  const kb = (n) => `${Math.round((n || 0) / 1024)} KB`;

  return (
    <section className="px-4 md:px-10 pt-6" data-testid="riff-library-card">
      <div className="border-2 border-zinc-700 bg-black/60 p-5 md:p-6">
        <div className="flex items-center gap-3 mb-4">
          <Music className="w-5 h-5 text-red-500" />
          <div className="text-[10px] tracking-[0.35em] text-red-500 uppercase">
            //_RIFF_LIBRARY
          </div>
          <span className="text-[10px] text-zinc-500 tracking-widest uppercase ml-auto">
            Background loops for jingles
          </span>
        </div>
        <h2 className="font-display text-2xl md:text-3xl text-zinc-50 leading-tight tracking-tight mb-1">
          THE <span className="text-red-600">RIFFS</span> UNDER THE DROPS.
        </h2>
        <p className="text-sm text-zinc-400 leading-relaxed max-w-2xl mb-5">
          Upload short instrumental loops (10-30 seconds is the sweet spot)
          and pick one as a background under any generated jingle. MP3, WAV,
          OGG, and M4A all accepted — the server re-encodes to 128kbps MP3
          so everything mixes cleanly. Max 5 MB per riff.
        </p>

        {/* Upload form */}
        <form
          onSubmit={handleUpload}
          className="border border-zinc-800 bg-zinc-950 p-3 mb-4 grid grid-cols-1 md:grid-cols-3 gap-3"
          data-testid="riff-upload-form"
        >
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value.slice(0, 80))}
            placeholder="Riff name · e.g. Fast Chug Loop"
            className="bg-black border border-zinc-800 focus:border-red-600 focus:outline-none px-3 py-2 text-sm text-zinc-100 font-mono"
            data-testid="riff-upload-name"
          />
          <input
            ref={fileInputRef}
            type="file"
            accept="audio/mpeg,audio/mp3,audio/wav,audio/ogg,audio/mp4,audio/x-m4a,audio/*"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="bg-black border border-zinc-800 focus:border-red-600 focus:outline-none px-3 py-1.5 text-xs text-zinc-300 file:bg-red-600 file:text-white file:border-0 file:px-3 file:py-1.5 file:mr-3 file:text-[10px] file:tracking-widest file:uppercase file:font-display hover:file:bg-red-500 file:cursor-pointer"
            data-testid="riff-upload-file"
          />
          <button
            type="submit"
            disabled={uploading || !name.trim() || !file}
            className="bg-red-600 text-white hover:bg-zinc-50 hover:text-black disabled:opacity-50 disabled:cursor-not-allowed font-display tracking-[0.25em] uppercase text-xs px-4 py-2 flex items-center justify-center gap-2 transition-colors"
            data-testid="riff-upload-submit"
          >
            <Upload className="w-4 h-4" strokeWidth={2.5} />
            {uploading ? "Uploading…" : "Upload Riff"}
          </button>
        </form>

        {/* Existing riffs */}
        {loading && (
          <div className="text-xs text-zinc-500 tracking-widest uppercase">
            //_loading_riffs…
          </div>
        )}
        {!loading && riffs.length === 0 && (
          <div
            className="text-xs text-zinc-500 tracking-widest uppercase border border-dashed border-zinc-800 p-6 text-center"
            data-testid="riff-library-empty"
          >
            //_no_riffs_yet · upload one above to unlock background music in the jingle studio
          </div>
        )}
        {!loading && riffs.length > 0 && (
          <ul className="space-y-2" data-testid="riff-library-list">
            {riffs.map((r) => (
              <li
                key={r.id}
                className="border border-zinc-800 bg-black/40 p-3 flex flex-col gap-2"
                data-testid={`riff-library-item-${r.id}`}
              >
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-zinc-100 font-mono truncate">
                      {r.name}
                    </div>
                    <div className="text-[10px] tracking-widest text-zinc-500 uppercase mt-0.5">
                      {kb(r.size_bytes)} · {new Date(r.created_at).toLocaleDateString()}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleDelete(r)}
                    className="shrink-0 border border-red-700 text-red-500 hover:bg-red-600 hover:text-white text-[10px] tracking-widest uppercase font-display px-3 py-1.5 flex items-center gap-1.5 transition-colors"
                    data-testid={`riff-library-delete-${r.id}`}
                  >
                    <Trash2 className="w-3 h-3" />
                    Delete
                  </button>
                </div>
                {r.audio_url && (
                  <audio
                    src={r.audio_url}
                    controls
                    preload="none"
                    className="w-full"
                    data-testid={`riff-library-audio-${r.id}`}
                  />
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}



/* ============================================================================
   SubmissionsInboxCard — admin review panel for /submit band submissions.
   ========================================================================= */

const STATUS_META = {
  pending: { label: "PENDING", color: "border-yellow-500 text-yellow-400 bg-yellow-500/10" },
  reviewed: { label: "REVIEWED", color: "border-blue-500 text-blue-400 bg-blue-500/10" },
  in_rotation: { label: "IN ROTATION", color: "border-red-600 text-red-400 bg-red-600/10" },
  declined: { label: "DECLINED", color: "border-zinc-600 text-zinc-400 bg-zinc-800/40" },
};

function SubmissionsInboxCard({ token, onUnauthorized }) {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending");
  const [audioCache, setAudioCache] = useState({}); // id -> data URL
  const [loadingAudio, setLoadingAudio] = useState({}); // id -> bool
  const [expanded, setExpanded] = useState({});
  const [notesDraft, setNotesDraft] = useState({});
  const authHeaders = { Authorization: `Bearer ${token}` };

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/submissions`, { headers: authHeaders });
      setSubmissions(res?.data?.submissions || []);
    } catch (err) {
      if (err?.response?.status === 401) onUnauthorized?.();
      toast.error("Couldn't load submissions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [token]);

  const loadAudio = async (id) => {
    if (audioCache[id] || loadingAudio[id]) return;
    setLoadingAudio((s) => ({ ...s, [id]: true }));
    try {
      const res = await axios.get(`${API}/admin/submissions/${id}/audio`, {
        headers: authHeaders,
      });
      setAudioCache((c) => ({ ...c, [id]: res.data.audio_url }));
    } catch (err) {
      toast.error("Couldn't load audio");
    } finally {
      setLoadingAudio((s) => ({ ...s, [id]: false }));
    }
  };

  const toggleExpanded = (id) => {
    setExpanded((e) => ({ ...e, [id]: !e[id] }));
    if (!audioCache[id]) loadAudio(id);
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(
        `${API}/admin/submissions/${id}`,
        { status },
        { headers: authHeaders },
      );
      setSubmissions((prev) =>
        prev.map((s) => (s.id === id ? { ...s, status } : s)),
      );
      toast.success(`Marked ${STATUS_META[status]?.label || status}`);
    } catch (err) {
      toast.error("Update failed");
    }
  };

  const saveNotes = async (id) => {
    const notes = notesDraft[id] ?? "";
    try {
      await axios.patch(
        `${API}/admin/submissions/${id}`,
        { admin_notes: notes },
        { headers: authHeaders },
      );
      setSubmissions((prev) =>
        prev.map((s) => (s.id === id ? { ...s, admin_notes: notes } : s)),
      );
      toast.success("Notes saved");
    } catch (err) {
      toast.error("Couldn't save notes");
    }
  };

  // Toggle the "uploaded to Live365 rotation" flag on an in_rotation
  // submission. Separate from status because approval and actual on-air
  // upload are two different manual steps that used to fall out of sync.
  const toggleLive365 = async (id, current) => {
    const next = !current;
    try {
      const res = await axios.patch(
        `${API}/admin/submissions/${id}`,
        { live365_uploaded: next },
        { headers: authHeaders },
      );
      setSubmissions((prev) =>
        prev.map((s) =>
          s.id === id
            ? {
                ...s,
                live365_uploaded: next,
                live365_uploaded_at: res?.data?.live365_uploaded_at || (next ? new Date().toISOString() : null),
              }
            : s,
        ),
      );
      toast.success(next ? "Marked uploaded to Live365 ✓" : "Marked NOT uploaded");
    } catch (err) {
      toast.error("Couldn't update Live365 status");
    }
  };

  const handleDelete = async (s) => {
    if (!window.confirm(`Delete submission from "${s.band_name}"?`)) return;
    try {
      await axios.delete(`${API}/admin/submissions/${s.id}`, {
        headers: authHeaders,
      });
      setSubmissions((prev) => prev.filter((x) => x.id !== s.id));
      toast("Deleted");
    } catch (err) {
      toast.error("Delete failed");
    }
  };

  const filtered =
    filter === "all"
      ? submissions
      : submissions.filter((s) => (s.status || "pending") === filter);

  const counts = submissions.reduce(
    (acc, s) => {
      const st = s.status || "pending";
      acc[st] = (acc[st] || 0) + 1;
      acc.all += 1;
      // Count approved bands that haven't been marked as uploaded to
      // Live365 yet — powers the amber warning banner.
      if (st === "in_rotation" && !s.live365_uploaded) {
        acc.pending_live365_upload += 1;
      }
      return acc;
    },
    { pending: 0, reviewed: 0, in_rotation: 0, declined: 0, all: 0, pending_live365_upload: 0 },
  );

  return (
    <section className="px-4 md:px-10 pt-6" data-testid="submissions-inbox-card">
      <div className="border-2 border-zinc-700 bg-black/60 p-5 md:p-6">
        <div className="flex items-center gap-3 mb-4 flex-wrap">
          <Inbox className="w-5 h-5 text-red-500" />
          <div className="text-[10px] tracking-[0.35em] text-red-500 uppercase">
            //_BAND_SUBMISSIONS
          </div>
          <span className="text-[10px] text-zinc-500 tracking-widest uppercase ml-auto">
            {counts.pending} pending · {counts.all} total
          </span>
        </div>

        {/* Live365 upload backlog warning — surfaces approved bands that
            have NOT been dropped into the actual on-air rotation yet. */}
        {counts.pending_live365_upload > 0 && (
          <div
            className="mb-4 border-2 border-amber-600 bg-amber-950/40 px-4 py-3 flex items-center gap-3 flex-wrap"
            data-testid="live365-backlog-banner"
          >
            <span className="inline-block w-2.5 h-2.5 rounded-full bg-amber-500 animate-pulse shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-xs tracking-widest uppercase font-display text-amber-300">
                ⚠ {counts.pending_live365_upload} approved{" "}
                {counts.pending_live365_upload === 1 ? "band" : "bands"} still
                waiting for Live365 upload
              </div>
              <div className="text-[11px] text-amber-200/70 mt-0.5">
                Filter to <strong>IN ROTATION</strong> and mark each track
                <span className="mx-1 inline-flex items-center border border-green-600 text-green-400 px-1.5 py-0.5 text-[9px] tracking-widest uppercase font-display">
                  ✓ ON LIVE365
                </span>
                once you&apos;ve dropped it into the on-air rotation so nothing
                gets forgotten.
              </div>
            </div>
            {filter !== "in_rotation" && (
              <button
                type="button"
                onClick={() => setFilter("in_rotation")}
                className="text-[10px] tracking-widest uppercase font-display px-3 py-1.5 border border-amber-500 text-amber-300 hover:bg-amber-500 hover:text-black transition-colors shrink-0"
                data-testid="live365-backlog-jump-btn"
              >
                Show list →
              </button>
            )}
          </div>
        )}
        <h2 className="font-display text-2xl md:text-3xl text-zinc-50 leading-tight tracking-tight mb-1">
          THE <span className="text-red-600">INBOX</span>.
        </h2>
        <p className="text-sm text-zinc-400 leading-relaxed max-w-2xl mb-5">
          Every submission from{" "}
          <a
            href="/submit"
            target="_blank"
            rel="noopener noreferrer"
            className="text-red-500 hover:text-white underline underline-offset-2"
          >
            /submit
          </a>{" "}
          lands here. Preview the audio, drop notes for other DJs, and move
          tracks through the review flow.
        </p>

        {/* Filter tabs */}
        <div
          className="grid grid-cols-5 gap-0 border border-zinc-800 mb-5"
          data-testid="submissions-filter-tabs"
        >
          {[
            { key: "pending", label: "Pending" },
            { key: "reviewed", label: "Reviewed" },
            { key: "in_rotation", label: "In Rotation" },
            { key: "declined", label: "Declined" },
            { key: "all", label: "All" },
          ].map((t, i) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setFilter(t.key)}
              className={`px-2 py-2 text-[10px] tracking-widest uppercase font-display transition-colors border-zinc-800 ${
                i > 0 ? "border-l" : ""
              } ${
                filter === t.key
                  ? "bg-red-600 text-white"
                  : "bg-black text-zinc-400 hover:text-red-500"
              }`}
              data-testid={`submissions-filter-${t.key}`}
            >
              {t.label} · {counts[t.key] || 0}
            </button>
          ))}
        </div>

        {loading && (
          <div className="text-xs text-zinc-500 tracking-widest uppercase">
            //_loading_inbox…
          </div>
        )}
        {!loading && filtered.length === 0 && (
          <div
            className="text-xs text-zinc-500 tracking-widest uppercase border border-dashed border-zinc-800 p-6 text-center"
            data-testid="submissions-empty"
          >
            //_no_submissions_in_this_bucket
          </div>
        )}
        {!loading && filtered.length > 0 && (
          <ul className="space-y-3" data-testid="submissions-list">
            {filtered.map((s) => {
              const meta = STATUS_META[s.status || "pending"];
              const isOpen = !!expanded[s.id];
              return (
                <li
                  key={s.id}
                  className="border border-zinc-800 bg-black/40"
                  data-testid={`submission-item-${s.id}`}
                >
                  {/* Row header */}
                  <button
                    type="button"
                    onClick={() => toggleExpanded(s.id)}
                    className="w-full text-left p-3 md:p-4 flex items-start gap-3 hover:bg-zinc-950 transition-colors"
                    data-testid={`submission-toggle-${s.id}`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span
                          className={`inline-flex items-center gap-1.5 px-1.5 py-0.5 text-[9px] tracking-widest uppercase font-display border ${meta.color}`}
                        >
                          {meta.label}
                        </span>
                        <span className="font-display text-base md:text-lg text-zinc-50 tracking-tight truncate">
                          {s.band_name}
                        </span>
                        <span className="text-zinc-500">—</span>
                        <span className="text-sm text-zinc-300 font-mono truncate">
                          {s.song_title}
                        </span>
                      </div>
                      <div className="mt-1 text-[10px] tracking-widest text-zinc-500 uppercase font-mono">
                        {s.genre} · {s.country} ·{" "}
                        {new Date(s.created_at).toLocaleDateString()}
                      </div>
                    </div>
                    <span className="text-zinc-500 shrink-0 text-xs tracking-widest uppercase font-display">
                      {isOpen ? "COLLAPSE" : "OPEN"}
                    </span>
                  </button>

                  {/* Expanded body */}
                  {isOpen && (
                    <div
                      className="border-t border-zinc-800 p-3 md:p-4 space-y-4 bg-zinc-950/40"
                      data-testid={`submission-body-${s.id}`}
                    >
                      {/* Audio preview */}
                      <div>
                        <div className="text-[10px] tracking-[0.3em] text-zinc-500 uppercase mb-2 font-display">
                          //_preview
                        </div>
                        {loadingAudio[s.id] && !audioCache[s.id] && (
                          <div className="text-xs text-zinc-500 tracking-widest uppercase">
                            //_loading_audio…
                          </div>
                        )}
                        {audioCache[s.id] && (
                          <audio
                            src={audioCache[s.id]}
                            controls
                            preload="metadata"
                            className="w-full"
                            data-testid={`submission-audio-${s.id}`}
                          />
                        )}
                        <div className="mt-1 text-[10px] tracking-widest text-zinc-600 uppercase font-mono">
                          {s.orig_filename} · {Math.round((s.size_bytes || 0) / 1024)} KB
                        </div>
                      </div>

                      {/* Contact + socials */}
                      <div className="grid md:grid-cols-2 gap-3">
                        <InfoRow label="Email" value={s.email} link={`mailto:${s.email}`} />
                        {s.social_facebook && (
                          <InfoRow label="Facebook" value={s.social_facebook} link={s.social_facebook} />
                        )}
                        {s.social_instagram && (
                          <InfoRow label="Instagram" value={s.social_instagram} link={s.social_instagram} />
                        )}
                        {s.social_twitter && (
                          <InfoRow label="X / Twitter" value={s.social_twitter} link={s.social_twitter} />
                        )}
                        {s.social_website && (
                          <InfoRow label="Website" value={s.social_website} link={s.social_website} />
                        )}
                        {s.social_other && (
                          <InfoRow label="Other" value={s.social_other} link={s.social_other} />
                        )}
                      </div>

                      {/* Band message */}
                      {s.message && (
                        <div>
                          <div className="text-[10px] tracking-[0.3em] text-zinc-500 uppercase mb-1 font-display">
                            //_band_message
                          </div>
                          <div className="text-sm text-zinc-300 whitespace-pre-wrap font-mono bg-black border border-zinc-800 p-3">
                            {s.message}
                          </div>
                        </div>
                      )}

                      {/* Admin notes */}
                      <div>
                        <div className="text-[10px] tracking-[0.3em] text-zinc-500 uppercase mb-1 font-display flex items-center gap-1.5">
                          <StickyNote className="w-3 h-3" />
                          //_admin_notes
                        </div>
                        <textarea
                          value={notesDraft[s.id] ?? s.admin_notes ?? ""}
                          onChange={(e) =>
                            setNotesDraft((d) => ({ ...d, [s.id]: e.target.value }))
                          }
                          rows={2}
                          placeholder="Internal notes for the team…"
                          className="w-full bg-black border border-zinc-800 focus:border-red-600 focus:outline-none px-3 py-2 text-xs text-zinc-100 font-mono"
                          data-testid={`submission-notes-${s.id}`}
                        />
                        <button
                          type="button"
                          onClick={() => saveNotes(s.id)}
                          className="mt-1 text-[10px] tracking-widest uppercase font-display text-red-500 hover:text-white"
                          data-testid={`submission-notes-save-${s.id}`}
                        >
                          Save notes →
                        </button>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-2 flex-wrap pt-2 border-t border-zinc-800">
                        {(s.status || "pending") === "in_rotation" && (
                          // Live365 upload checkbox — only visible for
                          // in-rotation bands. Turns from red-warning to
                          // green-check once uploaded so the admin can see
                          // at a glance what's still pending upload.
                          <button
                            type="button"
                            onClick={() =>
                              toggleLive365(s.id, !!s.live365_uploaded)
                            }
                            className={`text-[10px] tracking-widest uppercase font-display px-2.5 py-1.5 border transition-colors inline-flex items-center gap-1.5 ${
                              s.live365_uploaded
                                ? "border-green-600 text-green-500 hover:bg-green-600 hover:text-black"
                                : "border-amber-600 text-amber-400 hover:bg-amber-600 hover:text-black"
                            }`}
                            data-testid={`submission-live365-toggle-${s.id}`}
                            title={
                              s.live365_uploaded
                                ? "Already uploaded to Live365 rotation — click to un-mark"
                                : "Not yet uploaded to Live365 — click when you drop the track in rotation"
                            }
                          >
                            {s.live365_uploaded ? "✓ ON LIVE365" : "⚠ UPLOAD TO LIVE365"}
                          </button>
                        )}
                        {["pending", "reviewed", "in_rotation", "declined"]
                          .filter((st) => st !== (s.status || "pending"))
                          .map((st) => (
                            <button
                              key={st}
                              type="button"
                              onClick={() => updateStatus(s.id, st)}
                              className="text-[10px] tracking-widest uppercase font-display px-2.5 py-1.5 border border-zinc-700 text-zinc-300 hover:border-red-600 hover:text-red-500 transition-colors"
                              data-testid={`submission-set-${st}-${s.id}`}
                            >
                              → {STATUS_META[st].label}
                            </button>
                          ))}
                        <button
                          type="button"
                          onClick={() => handleDelete(s)}
                          className="ml-auto inline-flex items-center gap-1.5 text-[10px] tracking-widest uppercase font-display px-2.5 py-1.5 border border-red-700 text-red-500 hover:bg-red-600 hover:text-white transition-colors"
                          data-testid={`submission-delete-${s.id}`}
                        >
                          <Trash2 className="w-3 h-3" />
                          Delete
                        </button>
                      </div>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </section>
  );
}

function InfoRow({ label, value, link }) {
  return (
    <div className="border border-zinc-800 bg-black px-3 py-2">
      <div className="text-[9px] tracking-widest text-zinc-500 uppercase font-mono">
        {label}
      </div>
      {link ? (
        <a
          href={link.startsWith("http") || link.startsWith("mailto:") ? link : `https://${link}`}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-0.5 text-xs text-zinc-100 font-mono hover:text-red-500 transition-colors break-all flex items-center gap-1"
        >
          {value}
          <ExternalLink className="w-3 h-3 shrink-0 opacity-60" />
        </a>
      ) : (
        <div className="mt-0.5 text-xs text-zinc-100 font-mono break-all">
          {value}
        </div>
      )}
    </div>
  );
}


/* ============================================================================
   GigsInboxCard — admin review panel for band gig submissions.
   Filter tabs (Pending / Approved / Declined / All), inline approve/reject,
   quick-edit venue/date/city/notes, and delete. All submissions land as
   ``pending`` from the /submit-gig public form.
   ========================================================================= */
function GigsInboxCard({ token, onUnauthorized }) {
  const [gigs, setGigs] = useState(null);
  const [filter, setFilter] = useState("pending"); // pending | approved | declined | all
  const [loading, setLoading] = useState(false);
  const authHeaders = { Authorization: `Bearer ${token}` };

  const load = async () => {
    setLoading(true);
    try {
      const params = filter === "all" ? {} : { status: filter };
      const res = await axios.get(`${API}/admin/gigs`, {
        headers: authHeaders,
        params,
      });
      setGigs(Array.isArray(res?.data?.gigs) ? res.data.gigs : []);
    } catch (e) {
      if (e?.response?.status === 401) onUnauthorized?.();
      toast.error("Couldn't load gigs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [filter]);

  const patchGig = async (id, payload, successMsg) => {
    try {
      await axios.patch(`${API}/admin/gigs/${id}`, payload, {
        headers: authHeaders,
      });
      toast.success(successMsg);
      load();
    } catch (e) {
      if (e?.response?.status === 401) onUnauthorized?.();
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  const deleteGig = async (g) => {
    if (!window.confirm(`Delete this gig for ${g.band_name}? This can't be undone.`)) {
      return;
    }
    try {
      await axios.delete(`${API}/admin/gigs/${g.id}`, { headers: authHeaders });
      toast.success("Gig deleted");
      load();
    } catch (e) {
      if (e?.response?.status === 401) onUnauthorized?.();
      toast.error("Delete failed");
    }
  };

  const filters = [
    { key: "pending", label: "Pending" },
    { key: "approved", label: "Approved" },
    { key: "declined", label: "Declined" },
    { key: "all", label: "All" },
  ];

  return (
    <div className="border-2 border-zinc-700 bg-zinc-950" data-testid="admin-gigs-inbox">
      <div className="border-b border-zinc-800 px-4 py-3 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2 text-sm text-zinc-100 font-display uppercase tracking-widest">
          <Inbox className="w-4 h-4 text-red-500" strokeWidth={2.5} />
          //_GIGS_INBOX
        </div>
        <div className="flex items-center gap-1">
          {filters.map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => setFilter(f.key)}
              className={`px-3 py-1.5 text-[11px] tracking-widest uppercase font-display border transition-colors ${
                filter === f.key
                  ? "border-red-600 bg-red-600 text-white"
                  : "border-zinc-700 text-zinc-300 hover:text-red-500 hover:border-red-600"
              }`}
              data-testid={`admin-gigs-filter-${f.key}`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>
      <div className="p-4 space-y-3">
        {loading ? (
          <div className="text-center py-6 text-xs text-zinc-500 tracking-widest uppercase">
            //_loading…
          </div>
        ) : !gigs?.length ? (
          <div className="text-center py-6 text-xs text-zinc-500 tracking-widest uppercase">
            //_no_gigs_in_this_bucket
          </div>
        ) : (
          gigs.map((g) => (
            <GigRow
              key={g.id}
              gig={g}
              onApprove={() => patchGig(g.id, { status: "approved" }, "Gig approved — live on /family")}
              onDecline={() => patchGig(g.id, { status: "declined" }, "Gig declined")}
              onDelete={() => deleteGig(g)}
              onSaveEdits={(edits) => patchGig(g.id, edits, "Gig updated")}
            />
          ))
        )}
      </div>
    </div>
  );
}

function GigRow({ gig, onApprove, onDecline, onDelete, onSaveEdits }) {
  const [editing, setEditing] = useState(false);
  const [venue, setVenue] = useState(gig.venue || "");
  const [city, setCity] = useState(gig.city || "");
  const [stateCountry, setStateCountry] = useState(gig.state_country || "");
  const [gigDate, setGigDate] = useState(gig.gig_date || "");
  const [ticketUrl, setTicketUrl] = useState(gig.ticket_url || "");
  const [notes, setNotes] = useState(gig.notes || "");

  const location = [gig.city, gig.state_country].filter(Boolean).join(", ");
  const statusColor =
    gig.status === "approved"
      ? "text-red-400 border-red-600"
      : gig.status === "declined"
        ? "text-zinc-500 border-zinc-700"
        : "text-amber-400 border-amber-500";

  const submitEdits = () => {
    onSaveEdits({
      venue,
      city,
      state_country: stateCountry,
      gig_date: gigDate,
      ticket_url: ticketUrl,
      notes,
    });
    setEditing(false);
  };

  return (
    <div className="border border-zinc-800 bg-black p-3" data-testid={`admin-gig-${gig.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="font-display text-lg text-zinc-50 tracking-tight truncate">
            {gig.band_name}
          </div>
          <div className="text-xs text-zinc-400 mt-0.5 truncate">
            {gig.gig_date} · {gig.venue}
            {location ? ` · ${location}` : ""}
          </div>
          {gig.notes && (
            <div className="text-[11px] text-zinc-500 mt-1.5 line-clamp-2">
              {gig.notes}
            </div>
          )}
          {gig.ticket_url && (
            <a
              href={gig.ticket_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-[10px] text-red-400 hover:text-red-500 tracking-widest uppercase mt-1.5"
              data-testid={`admin-gig-tickets-${gig.id}`}
            >
              <ExternalLink className="w-2.5 h-2.5" /> tickets
            </a>
          )}
          {gig.submitter_email && (
            <div className="text-[10px] text-zinc-500 mt-1.5 truncate">
              submitter: {gig.submitter_email}
            </div>
          )}
        </div>
        <div
          className={`shrink-0 text-[10px] tracking-widest uppercase border px-2 py-0.5 font-display ${statusColor}`}
        >
          {gig.status}
        </div>
      </div>

      {editing && (
        <div className="mt-3 pt-3 border-t border-zinc-800 grid grid-cols-1 md:grid-cols-2 gap-2">
          <input
            className="bg-zinc-950 border border-zinc-700 focus:border-red-600 outline-none text-xs text-zinc-100 px-2 py-1.5"
            value={venue}
            onChange={(e) => setVenue(e.target.value)}
            placeholder="Venue"
            data-testid={`admin-gig-edit-venue-${gig.id}`}
          />
          <input
            type="date"
            className="bg-zinc-950 border border-zinc-700 focus:border-red-600 outline-none text-xs text-zinc-100 px-2 py-1.5"
            value={gigDate}
            onChange={(e) => setGigDate(e.target.value)}
            data-testid={`admin-gig-edit-date-${gig.id}`}
          />
          <input
            className="bg-zinc-950 border border-zinc-700 focus:border-red-600 outline-none text-xs text-zinc-100 px-2 py-1.5"
            value={city}
            onChange={(e) => setCity(e.target.value)}
            placeholder="City"
          />
          <input
            className="bg-zinc-950 border border-zinc-700 focus:border-red-600 outline-none text-xs text-zinc-100 px-2 py-1.5"
            value={stateCountry}
            onChange={(e) => setStateCountry(e.target.value)}
            placeholder="State/Country"
          />
          <input
            className="bg-zinc-950 border border-zinc-700 focus:border-red-600 outline-none text-xs text-zinc-100 px-2 py-1.5 md:col-span-2"
            value={ticketUrl}
            onChange={(e) => setTicketUrl(e.target.value)}
            placeholder="Tickets URL"
          />
          <textarea
            className="bg-zinc-950 border border-zinc-700 focus:border-red-600 outline-none text-xs text-zinc-100 px-2 py-1.5 md:col-span-2 resize-y"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Notes"
            rows={2}
          />
        </div>
      )}

      <div className="mt-3 pt-3 border-t border-zinc-800 flex items-center gap-1.5 flex-wrap">
        {gig.status !== "approved" && (
          <button
            type="button"
            onClick={onApprove}
            className="px-2.5 py-1 text-[10px] tracking-widest uppercase font-display border border-red-600 bg-red-600 text-white hover:bg-white hover:text-black transition-colors"
            data-testid={`admin-gig-approve-${gig.id}`}
          >
            APPROVE
          </button>
        )}
        {gig.status !== "declined" && (
          <button
            type="button"
            onClick={onDecline}
            className="px-2.5 py-1 text-[10px] tracking-widest uppercase font-display border border-zinc-700 text-zinc-300 hover:border-red-600 hover:text-red-500 transition-colors"
            data-testid={`admin-gig-decline-${gig.id}`}
          >
            DECLINE
          </button>
        )}
        {editing ? (
          <>
            <button
              type="button"
              onClick={submitEdits}
              className="px-2.5 py-1 text-[10px] tracking-widest uppercase font-display border border-red-600 bg-red-600/20 text-red-300 hover:bg-red-600 hover:text-white transition-colors"
              data-testid={`admin-gig-save-${gig.id}`}
            >
              <Save className="w-3 h-3 inline mr-1" /> SAVE
            </button>
            <button
              type="button"
              onClick={() => setEditing(false)}
              className="px-2.5 py-1 text-[10px] tracking-widest uppercase font-display border border-zinc-700 text-zinc-300 hover:border-red-600 hover:text-red-500 transition-colors"
            >
              CANCEL
            </button>
          </>
        ) : (
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="px-2.5 py-1 text-[10px] tracking-widest uppercase font-display border border-zinc-700 text-zinc-300 hover:border-red-600 hover:text-red-500 transition-colors"
            data-testid={`admin-gig-edit-${gig.id}`}
          >
            <Edit3 className="w-3 h-3 inline mr-1" /> EDIT
          </button>
        )}
        <button
          type="button"
          onClick={onDelete}
          className="ml-auto px-2.5 py-1 text-[10px] tracking-widest uppercase font-display border border-zinc-700 text-zinc-500 hover:border-red-600 hover:text-red-500 transition-colors"
          data-testid={`admin-gig-delete-${gig.id}`}
        >
          <Trash2 className="w-3 h-3 inline mr-1" /> DELETE
        </button>
      </div>
    </div>
  );
}

/* ============================================================================
   FamilyWelcomeCard — one-click bulk welcome email to in-rotation bands.
   Uses existing Resend integration. Tracks per-band via welcome_email_sent_at
   so bands don't get spammed twice unless the admin explicitly opts in with
   "resend to all".
   ========================================================================= */
function FamilyWelcomeCard({ token, onUnauthorized }) {
  const [stats, setStats] = useState(null);
  const [sending, setSending] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const authHeaders = { Authorization: `Bearer ${token}` };

  const load = async () => {
    try {
      const res = await axios.get(`${API}/admin/family/welcome-stats`, {
        headers: authHeaders,
      });
      setStats(res?.data || null);
    } catch (e) {
      if (e?.response?.status === 401) onUnauthorized?.();
    }
  };

  useEffect(() => {
    load();
  }, [token]);

  const send = async (resend) => {
    if (sending) return;
    const label = resend ? "ALL in-rotation bands (including previously emailed)" : "unsent bands";
    if (!window.confirm(`Send the DRR family welcome email to ${label}? This can't be undone.`)) {
      return;
    }
    setSending(true);
    setLastResult(null);
    try {
      const res = await axios.post(
        `${API}/admin/family/welcome-email`,
        { resend: !!resend },
        { headers: authHeaders },
      );
      setLastResult(res?.data || null);
      toast.success(
        `📬 Sent ${res?.data?.sent ?? 0}/${res?.data?.attempted ?? 0} welcome emails`,
      );
      load();
    } catch (e) {
      if (e?.response?.status === 401) onUnauthorized?.();
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="border-2 border-zinc-700 bg-zinc-950" data-testid="admin-family-welcome-card">
      <div className="border-b border-zinc-800 px-4 py-3 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2 text-sm text-zinc-100 font-display uppercase tracking-widest">
          <Mail className="w-4 h-4 text-red-500" strokeWidth={2.5} />
          //_FAMILY_WELCOME
        </div>
        <div className="text-[10px] text-zinc-500 tracking-widest uppercase font-display">
          //_ONE-TAP_ONBOARDING
        </div>
      </div>
      <div className="p-4 space-y-4">
        <p className="text-xs text-zinc-400 leading-relaxed">
          One-click sends every in-rotation band a personalized welcome email
          with a link to <span className="text-red-400">/submit-gig</span> so
          they can start posting their own shows to the Family page. Uses
          your Resend integration. Bands are only emailed once by default.
        </p>

        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <StatChip label="In rotation" value={stats.in_rotation} />
            <StatChip label="With email" value={stats.with_email} />
            <StatChip label="Welcomed" value={stats.sent} accent />
            <StatChip label="Unsent" value={stats.unsent} highlight />
          </div>
        )}

        <div className="flex items-center gap-2 flex-wrap pt-2">
          <button
            type="button"
            onClick={() => send(false)}
            disabled={sending || !stats?.unsent}
            className={`inline-flex items-center gap-2 px-3 py-2 text-[11px] tracking-widest uppercase font-display border transition-colors ${
              sending || !stats?.unsent
                ? "border-zinc-700 text-zinc-500 cursor-not-allowed"
                : "border-red-600 bg-red-600 text-white hover:bg-white hover:text-black"
            }`}
            data-testid="admin-welcome-send-unsent"
          >
            {sending ? (
              <span className="inline-block w-3 h-3 border-2 border-current border-r-transparent rounded-full animate-spin" />
            ) : (
              <Mail className="w-3 h-3" strokeWidth={2.5} />
            )}
            {stats?.unsent
              ? `SEND WELCOME TO ${stats.unsent} UNSENT BAND${stats.unsent === 1 ? "" : "S"}`
              : "NO UNSENT BANDS"}
          </button>
          <button
            type="button"
            onClick={() => send(true)}
            disabled={sending || !stats?.with_email}
            className={`inline-flex items-center gap-2 px-3 py-2 text-[11px] tracking-widest uppercase font-display border transition-colors ${
              sending || !stats?.with_email
                ? "border-zinc-700 text-zinc-500 cursor-not-allowed"
                : "border-zinc-700 text-zinc-300 hover:border-red-600 hover:text-red-500"
            }`}
            data-testid="admin-welcome-send-all"
            title="Resend to every in-rotation band, including those already welcomed"
          >
            RESEND TO ALL
          </button>
        </div>

        {lastResult && (
          <div
            className="mt-2 border border-zinc-800 bg-black p-3"
            data-testid="admin-welcome-last-result"
          >
            <div className="text-[11px] tracking-widest uppercase font-display text-red-500 mb-2">
              //_LAST_RUN · {lastResult.sent}/{lastResult.attempted} sent
            </div>
            <div className="max-h-40 overflow-y-auto flex flex-col gap-1">
              {(lastResult.results || []).map((r) => (
                <div
                  key={r.band_id}
                  className="flex items-center justify-between gap-2 text-[11px]"
                >
                  <span className="truncate text-zinc-300">
                    {r.band_name}{" "}
                    <span className="text-zinc-600">· {r.email || "no-email"}</span>
                  </span>
                  <span
                    className={`shrink-0 tracking-widest uppercase font-display px-1.5 py-0.5 border ${
                      r.sent
                        ? "border-red-600 text-red-400"
                        : "border-zinc-700 text-zinc-500"
                    }`}
                  >
                    {r.sent ? "sent" : "skip"}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function StatChip({ label, value, accent, highlight }) {
  const border = highlight
    ? "border-red-600"
    : accent
      ? "border-zinc-700"
      : "border-zinc-800";
  const valueColor = highlight ? "text-red-500" : accent ? "text-red-400" : "text-zinc-100";
  return (
    <div className={`border ${border} bg-black px-3 py-2 flex flex-col gap-0.5`}>
      <span className="text-[9px] tracking-[0.3em] uppercase text-zinc-500 font-display">
        {label}
      </span>
      <span className={`font-display text-2xl leading-none ${valueColor}`}>
        {value ?? "—"}
      </span>
    </div>
  );
}


/* ============================================================================
   WeeklyDigestCard — subscribers snapshot + one-click preview + send.
   The auto-loop fires every Monday 10:00 UTC, but admin can trigger manually
   whenever they want to hype a big week.
   ========================================================================= */
function WeeklyDigestCard({ token, onUnauthorized }) {
  const [stats, setStats] = useState(null);
  const [sending, setSending] = useState(false);
  const [lastRun, setLastRun] = useState(null);
  const authHeaders = { Authorization: `Bearer ${token}` };

  const load = async () => {
    try {
      const res = await axios.get(`${API}/admin/newsletter/stats`, {
        headers: authHeaders,
      });
      setStats(res?.data || null);
    } catch (e) {
      if (e?.response?.status === 401) onUnauthorized?.();
    }
  };

  useEffect(() => {
    load();
  }, [token]);

  const preview = () => {
    const url = `${API}/admin/newsletter/digest/preview`;
    // Open a new tab with the token attached via header — since we can't
    // set headers on window.open, redirect via a fetch → blob → objectURL.
    axios
      .get(url, { headers: authHeaders, responseType: "text" })
      .then((r) => {
        const blob = new Blob([r.data], { type: "text/html" });
        const objUrl = URL.createObjectURL(blob);
        window.open(objUrl, "_blank", "noopener,noreferrer");
      })
      .catch(() => toast.error("Couldn't load preview"));
  };

  const send = async () => {
    if (sending) return;
    if (
      !window.confirm(
        `Send this week's digest to ${stats?.subscribers_active ?? "all"} active subscriber(s)? This can't be undone.`,
      )
    ) {
      return;
    }
    setSending(true);
    setLastRun(null);
    try {
      const res = await axios.post(
        `${API}/admin/newsletter/digest/send`,
        {},
        { headers: authHeaders },
      );
      setLastRun(res?.data || null);
      toast.success(
        `📬 Digest sent to ${res?.data?.sent ?? 0}/${res?.data?.attempted ?? 0} subscribers`,
      );
      load();
    } catch (e) {
      if (e?.response?.status === 401) onUnauthorized?.();
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  const lastSent = stats?.last_sent_at
    ? new Date(stats.last_sent_at).toLocaleString()
    : "never";

  return (
    <div
      className="border-2 border-zinc-700 bg-zinc-950"
      data-testid="admin-weekly-digest-card"
    >
      <div className="border-b border-zinc-800 px-4 py-3 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2 text-sm text-zinc-100 font-display uppercase tracking-widest">
          <Mail className="w-4 h-4 text-red-500" strokeWidth={2.5} />
          //_WEEKLY_GIG_DIGEST
        </div>
        <div className="text-[10px] text-zinc-500 tracking-widest uppercase font-display">
          AUTO: MON 10:00 UTC · last: {lastSent}
        </div>
      </div>
      <div className="p-4 space-y-4">
        <p className="text-xs text-zinc-400 leading-relaxed">
          Every Monday at 10:00 UTC the digest auto-fires to every active
          subscriber, listing every approved show for the coming 7 days
          grouped by band. Preview or send manually anytime below.
        </p>

        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            <StatChip label="Subs (active)" value={stats.subscribers_active} accent />
            <StatChip label="Unsub'd" value={stats.subscribers_unsub} />
            <StatChip label="Total ever" value={stats.subscribers_total} />
            <StatChip
              label="Gigs (7d)"
              value={stats.gigs_this_week}
              highlight={stats.gigs_this_week > 0}
            />
            <StatChip label="Bands (7d)" value={stats.bands_this_week} />
          </div>
        )}

        <div className="flex items-center gap-2 flex-wrap pt-2">
          <button
            type="button"
            onClick={preview}
            className="inline-flex items-center gap-2 px-3 py-2 text-[11px] tracking-widest uppercase font-display border border-zinc-700 text-zinc-300 hover:border-red-600 hover:text-red-500 transition-colors"
            data-testid="admin-digest-preview"
          >
            <ExternalLink className="w-3 h-3" strokeWidth={2.5} />
            PREVIEW EMAIL
          </button>
          <button
            type="button"
            onClick={send}
            disabled={sending || !stats?.subscribers_active}
            className={`inline-flex items-center gap-2 px-3 py-2 text-[11px] tracking-widest uppercase font-display border transition-colors ${
              sending || !stats?.subscribers_active
                ? "border-zinc-700 text-zinc-500 cursor-not-allowed"
                : "border-red-600 bg-red-600 text-white hover:bg-white hover:text-black"
            }`}
            data-testid="admin-digest-send-now"
          >
            {sending ? (
              <span className="inline-block w-3 h-3 border-2 border-current border-r-transparent rounded-full animate-spin" />
            ) : (
              <Mail className="w-3 h-3" strokeWidth={2.5} />
            )}
            {stats?.subscribers_active
              ? `SEND NOW · ${stats.subscribers_active} SUB${stats.subscribers_active === 1 ? "" : "S"}`
              : "NO SUBSCRIBERS YET"}
          </button>
        </div>

        {lastRun && (
          <div
            className="border border-zinc-800 bg-black p-3"
            data-testid="admin-digest-last-run"
          >
            <div className="text-[11px] tracking-widest uppercase font-display text-red-500 mb-2">
              //_LAST_RUN · sent {lastRun.sent}/{lastRun.attempted} · covered{" "}
              {lastRun.gigs} gigs across {lastRun.bands} bands
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


/* ============================================================================
   DailyPulseCard — always-visible pulse dashboard at top of /admin.
   Refreshes every 30s. Tells the admin at a glance whether the current
   social push / release is actually converting.
   ========================================================================= */
function DailyPulseCard({ token, onUnauthorized }) {
  const [pulse, setPulse] = useState(null);
  const [tick, setTick] = useState(0); // forces a subtle heartbeat animation
  const [emailingNow, setEmailingNow] = useState(false);
  const authHeaders = { Authorization: `Bearer ${token}` };

  const load = async () => {
    try {
      const res = await axios.get(`${API}/admin/daily-pulse`, {
        headers: authHeaders,
      });
      setPulse(res?.data || null);
      setTick((t) => t + 1);
    } catch (e) {
      if (e?.response?.status === 401) onUnauthorized?.();
    }
  };

  const emailNow = async () => {
    if (emailingNow) return;
    setEmailingNow(true);
    try {
      const res = await axios.post(
        `${API}/admin/daily-pulse/email-now`,
        {},
        { headers: authHeaders },
      );
      if (res?.data?.sent) {
        toast.success("📬 Daily pulse email sent to your admin inbox");
      } else {
        toast.error(
          res?.data?.reason === "missing_config"
            ? "Set SUBMISSIONS_RECIPIENT_EMAIL + RESEND_API_KEY to enable this"
            : "Email failed — check backend logs",
        );
      }
    } catch (e) {
      if (e?.response?.status === 401) onUnauthorized?.();
      toast.error("Email failed");
    } finally {
      setEmailingNow(false);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 30 * 1000);
    return () => clearInterval(t);
  }, [token]);

  const today = pulse?.today || {};
  const totals = pulse?.totals || {};
  const live = pulse?.live || {};
  const lastHour = pulse?.last_hour || {};
  const queues = pulse?.queues || {};
  const anyQueue = (queues.pending_gigs || 0) + (queues.pending_submissions || 0);
  const generated = pulse?.generated_at
    ? new Date(pulse.generated_at).toLocaleTimeString()
    : "—";

  return (
    <div
      className="border-2 border-red-600 bg-zinc-950"
      data-testid="admin-daily-pulse-card"
    >
      <div className="border-b border-zinc-800 px-4 py-3 flex items-center justify-between flex-wrap gap-3 bg-red-600/10">
        <div className="flex items-center gap-2 text-sm text-zinc-100 font-display uppercase tracking-widest">
          <span
            key={tick}
            className="inline-block w-2 h-2 rounded-full bg-red-500 animate-pulse"
            aria-hidden
          />
          //_DAILY_PULSE
        </div>
        <div className="flex items-center gap-3 text-[10px] tracking-widest uppercase text-zinc-500 font-display">
          <span>REFRESHED: {generated}</span>
          <button
            type="button"
            onClick={emailNow}
            disabled={emailingNow}
            className={`inline-flex items-center gap-1 transition-colors ${
              emailingNow
                ? "text-zinc-600 cursor-wait"
                : "text-zinc-300 hover:text-red-500"
            }`}
            data-testid="admin-pulse-email-now"
            title="Send this pulse snapshot to your admin inbox now"
          >
            <Mail className="w-3 h-3" strokeWidth={2.5} />
            {emailingNow ? "…" : "EMAIL ME NOW"}
          </button>
          <button
            type="button"
            onClick={load}
            className="text-zinc-300 hover:text-red-500 transition-colors"
            data-testid="admin-pulse-refresh"
            title="Refresh now"
          >
            ↻ REFRESH
          </button>
        </div>
      </div>

      <div className="p-4">
        {/* Row 1 — TODAY's engagement */}
        <div className="text-[10px] tracking-[0.35em] text-red-500 uppercase font-display mb-2">
          //_TODAY (since 00:00 UTC)
        </div>
        <div className="grid grid-cols-3 md:grid-cols-6 gap-2 mb-4">
          <PulseTile
            label="New rooters"
            value={today.supporters}
            hot={today.supporters > 0}
            testId="pulse-today-supporters"
          />
          <PulseTile
            label="New subs"
            value={today.subscribers}
            hot={today.subscribers > 0}
            testId="pulse-today-subs"
          />
          <PulseTile
            label="Visits"
            value={today.visits}
            testId="pulse-today-visits"
          />
          <PulseTile
            label="Chat msgs"
            value={today.chat_messages}
            testId="pulse-today-chat"
          />
          <PulseTile
            label="Hearts"
            value={today.track_hearts}
            testId="pulse-today-hearts"
          />
          <PulseTile
            label="Gigs posted"
            value={today.gig_submissions}
            testId="pulse-today-gigs"
          />
        </div>

        {/* Row 2 — LIVE + LAST HOUR + TOTALS */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
          <PulseTile
            label="Live listeners"
            value={live.listeners}
            hot={live.listeners > 0}
            emphasis
            testId="pulse-live-listeners"
          />
          <PulseTile
            label="Rooters (last 60m)"
            value={lastHour.supporters}
            hot={lastHour.supporters > 0}
            emphasis
            testId="pulse-hour-supporters"
          />
          <PulseTile
            label="Rooters total"
            value={totals.supporters}
            muted
            testId="pulse-total-supporters"
          />
          <PulseTile
            label="Subs total"
            value={totals.subscribers}
            muted
            testId="pulse-total-subs"
          />
        </div>

        {/* Queue callouts — only if there's something to action */}
        {anyQueue > 0 && (
          <div
            className="border border-amber-500/60 bg-amber-500/10 px-3 py-2 flex items-center justify-between flex-wrap gap-2"
            data-testid="pulse-queue-callout"
          >
            <div className="text-[11px] tracking-widest uppercase text-amber-400 font-display">
              //_WAITING_ON_YOU:
            </div>
            <div className="flex items-center gap-3 text-[11px] tracking-widest uppercase font-display text-zinc-100">
              {queues.pending_submissions > 0 && (
                <span>
                  <span className="text-amber-400">
                    {queues.pending_submissions}
                  </span>{" "}
                  music sub{queues.pending_submissions === 1 ? "" : "s"}
                </span>
              )}
              {queues.pending_gigs > 0 && (
                <span>
                  <span className="text-amber-400">{queues.pending_gigs}</span>{" "}
                  gig{queues.pending_gigs === 1 ? "" : "s"}
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function PulseTile({ label, value, hot, emphasis, muted, testId }) {
  const shown = value === null || value === undefined ? "—" : value.toLocaleString();
  const borderColor = hot ? "border-red-600" : emphasis ? "border-zinc-700" : "border-zinc-800";
  const valueColor = hot
    ? "text-red-500"
    : emphasis
      ? "text-zinc-50"
      : muted
        ? "text-zinc-400"
        : "text-zinc-100";
  const bg = hot ? "bg-red-600/10" : "bg-black";
  return (
    <div
      className={`border ${borderColor} ${bg} px-3 py-2 flex flex-col gap-0.5`}
      data-testid={testId}
    >
      <span className="text-[9px] tracking-[0.28em] uppercase text-zinc-500 font-display truncate">
        {label}
      </span>
      <span className={`font-display text-2xl leading-none tabular-nums ${valueColor}`}>
        {shown}
      </span>
    </div>
  );
}

